import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { auraApi, getVisitorId, randomId, storage } from "../../lib/aura";
import { toSpeech } from "./SimmiMarkdown";

const AuraContext = createContext(null);

// Simmi speaks English and the major Indian languages. Everything she says on her own (not from the backend) is
// here: English and Hindi in full, the others in MORE_TEXT below.
export const LANGUAGES = ["en", "hi", "ta", "te", "kn", "ml", "mr", "bn", "gu", "pa", "or"];
export const TEXT = {
  en: {
    greeting: "Hi, I'm Simmi — Simpel's AI guide. Ask me anything about how AI could work for your business, or tell me what you're trying to solve.",
    voiceGreeting: "Hi, I'm Simmi, Simpel's AI guide. What would you like AI to help your business with?",
    clarify: "That didn't come through clearly. Could you say that again, and tell me what you'd like to focus on?",
    nudge: "Just checking in gently, are you still there? If you want, I can keep it very simple and explain how Simpel helps turn AI into real business results.",
    nudgeMeeting: "Just checking in, are you still there? If it helps, I can set up a quick call with one of our AI strategists.",
    end: "Since we haven't spoken for a while, I'll be ending this session. Feel free to call back when you're ready.",
    micDenied: "I need microphone access to talk. You can allow it in your browser, or just type below.",
    listening: "I'm listening. What would you like to know?",
    bookMeetingAsk: "I'd like to book a meeting with your team.",
    starters: ["Find my AI opportunities", "What is Cognitive AI?", "How does an engagement work?", "Talk to an AI strategist"],
    askFollowUp: "Ask a follow-up",
    askAnything: "Ask Simmi anything…",
    sendingWhenDone: "Sending when Simmi finishes…",
    switchTo: "हिंदी में बात करें",
    // What Simmi is doing while a reply is on its way (keys: the backend's status events)
    status: {
      thinking: "Thinking…",
      show_card: "Pulling up the right example…",
      show_page: "Finding the right page…",
      offer_meeting: "Checking the team's calendar…",
      start_opportunity_finder: "Setting up your opportunity finder…",
      save_lead_details: "Noting that down…",
      suggest_followups: "Thinking of what you might ask next…",
    },
    explore: "Explore",
    copy: "Copy reply",
    copied: "Copied",
    helpful: "Helpful",
    notHelpful: "Not helpful",
    thanksFeedback: "Thanks for the feedback",
    dictate: "Hold to dictate, or tap to start and stop",
    dictating: "Listening… release to stop",
    shortcut: "Ask Simmi (Ctrl/⌘ K)",
    finder: {
      title: "AI Opportunity Finder",
      back: "Back",
      stepOf: (n, total) => `Step ${n} of ${total}`,
      questions: {
        industry: "Which industry are you in?",
        focus: "Where does it hurt most today?",
        team_size: "How big is your team?",
      },
      options: {
        industry: [
          { v: "manufacturing", label: "Manufacturing" },
          { v: "bfsi", label: "Banking & finance" },
          { v: "healthcare", label: "Healthcare" },
          { v: "education", label: "Education" },
          { v: "logistics", label: "Logistics" },
          { v: "other", label: "Something else" },
        ],
        focus: [
          { v: "sales", label: "Sales & support" },
          { v: "hr", label: "HR & people" },
          { v: "finance", label: "Finance" },
          { v: "operations", label: "Operations" },
          { v: "unsure", label: "Not sure yet" },
        ],
        team_size: [
          { v: "<50", label: "Under 50" },
          { v: "50-200", label: "50–200" },
          { v: "200-1000", label: "200–1,000" },
          { v: "1000+", label: "1,000+" },
        ],
      },
      compose: (industry, focus, team) =>
        `Opportunity Finder: ${industry}, focus on ${focus.toLowerCase()}, team size ${team}. What are my top AI opportunities?`,
      summary: (industry, focus, team) => `${industry} · ${focus} · ${team}`,
    },
  },
  hi: {
    greeting: "नमस्ते, मैं सिम्मी हूँ, सिम्पल की AI गाइड। AI आपके बिज़नेस में कैसे काम आ सकता है, इस बारे में कुछ भी पूछिए, या बताइए कि आप किस समस्या का हल चाहते हैं।",
    voiceGreeting: "नमस्ते! मैं सिम्मी हूँ, सिम्पल की AI गाइड। आप AI से अपने बिज़नेस में किस चीज़ में मदद चाहेंगे?",
    clarify: "माफ़ कीजिए, ये साफ़ सुनाई नहीं दिया। क्या आप एक बार फिर बता सकते हैं कि आप किस बारे में जानना चाहते हैं?",
    nudge: "क्या आप अभी भी यहाँ हैं? अगर आप चाहें, तो मैं बहुत आसान शब्दों में बता सकती हूँ कि सिम्पल AI को असली बिज़नेस नतीजों में कैसे बदलता है।",
    nudgeMeeting: "क्या आप अभी भी यहाँ हैं? अगर मददगार हो, तो मैं हमारे किसी AI strategist के साथ एक छोटी सी call तय कर सकती हूँ।",
    end: "काफ़ी देर से बात नहीं हुई, इसलिए मैं ये session बंद कर रही हूँ। जब भी आप तैयार हों, फिर से बात कीजिए।",
    micDenied: "बात करने के लिए मुझे माइक्रोफ़ोन की अनुमति चाहिए। आप अपने browser में अनुमति दे सकते हैं, या नीचे लिखकर पूछ सकते हैं।",
    listening: "मैं सुन रही हूँ। आप क्या जानना चाहेंगे?",
    bookMeetingAsk: "मैं आपकी टीम के साथ एक मीटिंग बुक करना चाहता हूँ।",
    starters: ["मेरे लिए AI के मौके खोजें", "Cognitive AI क्या है?", "आप कैसे काम करते हैं?", "AI strategist से बात करें"],
    askFollowUp: "अगला सवाल पूछिए",
    askAnything: "सिम्मी से कुछ भी पूछिए…",
    sendingWhenDone: "सिम्मी के बोलने के बाद भेजा जाएगा…",
    switchTo: "Talk in English",
    status: {
      thinking: "सोच रही हूँ…",
      show_card: "सही उदाहरण ढूँढ रही हूँ…",
      show_page: "सही page ढूँढ रही हूँ…",
      offer_meeting: "टीम का calendar देख रही हूँ…",
      start_opportunity_finder: "आपका opportunity finder तैयार कर रही हूँ…",
      save_lead_details: "नोट कर रही हूँ…",
      suggest_followups: "आपके अगले सवाल सोच रही हूँ…",
    },
    explore: "देखें",
    copy: "जवाब copy करें",
    copied: "Copy हो गया",
    helpful: "मददगार",
    notHelpful: "मददगार नहीं",
    thanksFeedback: "आपकी राय के लिए धन्यवाद",
    dictate: "बोलकर लिखने के लिए दबाकर रखें, या tap करके शुरू और बंद करें",
    dictating: "सुन रही हूँ… छोड़ने पर रुकेगा",
    shortcut: "सिम्मी से पूछें (Ctrl/⌘ K)",
    finder: {
      title: "AI Opportunity Finder",
      back: "पीछे",
      stepOf: (n, total) => `${total} में से कदम ${n}`,
      questions: {
        industry: "आपकी industry कौन सी है?",
        focus: "आज सबसे ज़्यादा दिक्कत कहाँ है?",
        team_size: "आपकी टीम कितनी बड़ी है?",
      },
      options: {
        industry: [
          { v: "manufacturing", label: "Manufacturing" },
          { v: "bfsi", label: "Banking और finance" },
          { v: "healthcare", label: "Healthcare" },
          { v: "education", label: "Education" },
          { v: "logistics", label: "Logistics" },
          { v: "other", label: "कुछ और" },
        ],
        focus: [
          { v: "sales", label: "Sales और support" },
          { v: "hr", label: "HR और लोग" },
          { v: "finance", label: "Finance" },
          { v: "operations", label: "Operations" },
          { v: "unsure", label: "अभी पक्का नहीं" },
        ],
        team_size: [
          { v: "<50", label: "50 से कम" },
          { v: "50-200", label: "50–200" },
          { v: "200-1000", label: "200–1,000" },
          { v: "1000+", label: "1,000+" },
        ],
      },
      compose: (industry, focus, team) =>
        `Opportunity Finder: ${industry}, focus ${focus}, टीम ${team}। मेरे लिए AI के सबसे अच्छे मौके क्या हैं?`,
      summary: (industry, focus, team) => `${industry} · ${focus} · ${team}`,
    },
  },
};
TEXT.en.pickLanguage = "Which language would you like to talk in?";
TEXT.en.language = "Language";
TEXT.hi.pickLanguage = "आप किस भाषा में बात करना चाहेंगे?";
TEXT.hi.language = "भाषा";

// The other Indian languages: Simmi's own spoken and visible lines. Anything not listed (buttons, the finder's
// chips) stays in English. Her answers come from the backend in the visitor's language.
const MORE_TEXT = {
  ta: {
    greeting: "வணக்கம், நான் சிம்மி, சிம்பலின் AI வழிகாட்டி. உங்கள் வணிகத்தில் AI எப்படி உதவும் என்று எதையும் கேளுங்கள்.",
    voiceGreeting: "வணக்கம்! நான் சிம்மி, சிம்பலின் AI வழிகாட்டி. உங்கள் வணிகத்தில் AI எதில் உதவ வேண்டும் என்று விரும்புகிறீர்கள்?",
    clarify: "மன்னிக்கவும், அது தெளிவாகக் கேட்கவில்லை. மீண்டும் ஒருமுறை சொல்ல முடியுமா?",
    nudge: "இன்னும் இருக்கிறீர்களா? விரும்பினால், சிம்பல் AI-ஐ எப்படி உண்மையான வணிகப் பலன்களாக மாற்றுகிறது என்று எளிமையாகச் சொல்கிறேன்.",
    nudgeMeeting: "இன்னும் இருக்கிறீர்களா? விரும்பினால், எங்கள் AI strategist ஒருவருடன் ஒரு சிறிய call ஏற்பாடு செய்கிறேன்.",
    end: "நீண்ட நேரமாகப் பேசவில்லை, அதனால் இந்த session-ஐ முடிக்கிறேன். தயாரானதும் மீண்டும் பேசுங்கள்.",
    micDenied: "பேச எனக்கு microphone அனுமதி தேவை. Browser-இல் அனுமதி கொடுக்கலாம், அல்லது கீழே type செய்யலாம்.",
    listening: "நான் கேட்கிறேன். என்ன தெரிந்துகொள்ள விரும்புகிறீர்கள்?",
    starters: ["என் AI வாய்ப்புகளைக் கண்டுபிடி", "Cognitive AI என்றால் என்ன?", "நீங்கள் எப்படி வேலை செய்கிறீர்கள்?", "AI strategist-உடன் பேச வேண்டும்"],
    askFollowUp: "அடுத்த கேள்வியைக் கேளுங்கள்",
    askAnything: "சிம்மியிடம் எதையும் கேளுங்கள்…",
    status: { thinking: "யோசிக்கிறேன்…" },
    pickLanguage: "எந்த மொழியில் பேச விரும்புகிறீர்கள்?",
  },
  te: {
    greeting: "నమస్కారం, నేను సిమ్మీ, సింపెల్ AI గైడ్‌ని. మీ వ్యాపారానికి AI ఎలా ఉపయోగపడుతుందో ఏదైనా అడగండి.",
    voiceGreeting: "నమస్కారం! నేను సిమ్మీ, సింపెల్ AI గైడ్‌ని. మీ వ్యాపారంలో AI దేనికి సహాయం చేయాలని మీరు కోరుకుంటున్నారు?",
    clarify: "క్షమించండి, అది స్పష్టంగా వినిపించలేదు. మరోసారి చెప్పగలరా?",
    nudge: "మీరు ఇంకా ఉన్నారా? కావాలంటే, సింపెల్ AIని నిజమైన వ్యాపార ఫలితాలుగా ఎలా మారుస్తుందో సులభంగా చెబుతాను.",
    nudgeMeeting: "మీరు ఇంకా ఉన్నారా? కావాలంటే, మా AI strategistతో ఒక చిన్న call ఏర్పాటు చేస్తాను.",
    end: "చాలాసేపటిగా మాట్లాడలేదు, అందుకే ఈ session ముగిస్తున్నాను. సిద్ధమైనప్పుడు మళ్ళీ మాట్లాడండి.",
    micDenied: "మాట్లాడటానికి నాకు microphone అనుమతి కావాలి. Browserలో అనుమతి ఇవ్వండి, లేదా కింద type చేయండి.",
    listening: "నేను వింటున్నాను. మీరు ఏం తెలుసుకోవాలనుకుంటున్నారు?",
    starters: ["నా AI అవకాశాలను కనుగొను", "Cognitive AI అంటే ఏమిటి?", "మీరు ఎలా పని చేస్తారు?", "AI strategistతో మాట్లాడాలి"],
    askFollowUp: "తదుపరి ప్రశ్న అడగండి",
    askAnything: "సిమ్మీని ఏదైనా అడగండి…",
    status: { thinking: "ఆలోచిస్తున్నాను…" },
    pickLanguage: "మీరు ఏ భాషలో మాట్లాడాలనుకుంటున్నారు?",
  },
  kn: {
    greeting: "ನಮಸ್ಕಾರ, ನಾನು ಸಿಮ್ಮಿ, ಸಿಂಪೆಲ್‌ನ AI ಮಾರ್ಗದರ್ಶಿ. ನಿಮ್ಮ ವ್ಯವಹಾರಕ್ಕೆ AI ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು ಎಂದು ಏನು ಬೇಕಾದರೂ ಕೇಳಿ.",
    voiceGreeting: "ನಮಸ್ಕಾರ! ನಾನು ಸಿಮ್ಮಿ, ಸಿಂಪೆಲ್‌ನ AI ಮಾರ್ಗದರ್ಶಿ. ನಿಮ್ಮ ವ್ಯವಹಾರದಲ್ಲಿ AI ಯಾವುದಕ್ಕೆ ಸಹಾಯ ಮಾಡಬೇಕೆಂದು ಬಯಸುತ್ತೀರಿ?",
    clarify: "ಕ್ಷಮಿಸಿ, ಅದು ಸ್ಪಷ್ಟವಾಗಿ ಕೇಳಿಸಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಇನ್ನೊಮ್ಮೆ ಹೇಳುತ್ತೀರಾ?",
    nudge: "ನೀವು ಇನ್ನೂ ಇದ್ದೀರಾ? ಬೇಕಾದರೆ, ಸಿಂಪೆಲ್ AI ಅನ್ನು ನಿಜವಾದ ವ್ಯವಹಾರ ಫಲಿತಾಂಶಗಳಾಗಿ ಹೇಗೆ ಬದಲಾಯಿಸುತ್ತದೆ ಎಂದು ಸರಳವಾಗಿ ಹೇಳುತ್ತೇನೆ.",
    nudgeMeeting: "ನೀವು ಇನ್ನೂ ಇದ್ದೀರಾ? ಬೇಕಾದರೆ, ನಮ್ಮ AI strategist ಜೊತೆ ಒಂದು ಚಿಕ್ಕ call ಏರ್ಪಡಿಸುತ್ತೇನೆ.",
    end: "ತುಂಬಾ ಹೊತ್ತಿನಿಂದ ಮಾತನಾಡಿಲ್ಲ, ಹಾಗಾಗಿ ಈ session ಮುಗಿಸುತ್ತಿದ್ದೇನೆ. ಸಿದ್ಧವಾದಾಗ ಮತ್ತೆ ಮಾತನಾಡಿ.",
    micDenied: "ಮಾತನಾಡಲು ನನಗೆ microphone ಅನುಮತಿ ಬೇಕು. Browser ನಲ್ಲಿ ಅನುಮತಿ ನೀಡಿ, ಅಥವಾ ಕೆಳಗೆ type ಮಾಡಿ.",
    listening: "ನಾನು ಕೇಳುತ್ತಿದ್ದೇನೆ. ನೀವು ಏನು ತಿಳಿಯಲು ಬಯಸುತ್ತೀರಿ?",
    starters: ["ನನ್ನ AI ಅವಕಾಶಗಳನ್ನು ಹುಡುಕಿ", "Cognitive AI ಎಂದರೇನು?", "ನೀವು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತೀರಿ?", "AI strategist ಜೊತೆ ಮಾತನಾಡಬೇಕು"],
    askFollowUp: "ಮುಂದಿನ ಪ್ರಶ್ನೆ ಕೇಳಿ",
    askAnything: "ಸಿಮ್ಮಿಯನ್ನು ಏನು ಬೇಕಾದರೂ ಕೇಳಿ…",
    status: { thinking: "ಯೋಚಿಸುತ್ತಿದ್ದೇನೆ…" },
    pickLanguage: "ನೀವು ಯಾವ ಭಾಷೆಯಲ್ಲಿ ಮಾತನಾಡಲು ಬಯಸುತ್ತೀರಿ?",
  },
  ml: {
    greeting: "നമസ്കാരം, ഞാൻ സിമ്മി, സിംപെലിന്റെ AI ഗൈഡ്. നിങ്ങളുടെ ബിസിനസിന് AI എങ്ങനെ സഹായിക്കുമെന്ന് എന്തും ചോദിക്കൂ.",
    voiceGreeting: "നമസ്കാരം! ഞാൻ സിമ്മി, സിംപെലിന്റെ AI ഗൈഡ്. നിങ്ങളുടെ ബിസിനസിൽ AI എന്തിന് സഹായിക്കണമെന്നാണ് ആഗ്രഹം?",
    clarify: "ക്ഷമിക്കണം, അത് വ്യക്തമായി കേട്ടില്ല. ഒന്നുകൂടി പറയാമോ?",
    nudge: "നിങ്ങൾ ഇപ്പോഴും ഇവിടെയുണ്ടോ? വേണമെങ്കിൽ, സിംപെൽ AI-യെ യഥാർത്ഥ ബിസിനസ് ഫലങ്ങളാക്കി മാറ്റുന്നത് എങ്ങനെയെന്ന് ലളിതമായി പറയാം.",
    nudgeMeeting: "നിങ്ങൾ ഇപ്പോഴും ഇവിടെയുണ്ടോ? വേണമെങ്കിൽ, ഞങ്ങളുടെ AI strategist-നൊപ്പം ഒരു ചെറിയ call ഏർപ്പാടാക്കാം.",
    end: "കുറേ നേരമായി സംസാരിച്ചിട്ടില്ല, അതിനാൽ ഈ session അവസാനിപ്പിക്കുന്നു. തയ്യാറാകുമ്പോൾ വീണ്ടും സംസാരിക്കൂ.",
    micDenied: "സംസാരിക്കാൻ എനിക്ക് microphone അനുമതി വേണം. Browser-ൽ അനുമതി നൽകൂ, അല്ലെങ്കിൽ താഴെ type ചെയ്യൂ.",
    listening: "ഞാൻ കേൾക്കുന്നുണ്ട്. എന്താണ് അറിയേണ്ടത്?",
    starters: ["എന്റെ AI അവസരങ്ങൾ കണ്ടെത്തൂ", "Cognitive AI എന്താണ്?", "നിങ്ങൾ എങ്ങനെയാണ് പ്രവർത്തിക്കുന്നത്?", "AI strategist-നോട് സംസാരിക്കണം"],
    askFollowUp: "അടുത്ത ചോദ്യം ചോദിക്കൂ",
    askAnything: "സിമ്മിയോട് എന്തും ചോദിക്കൂ…",
    status: { thinking: "ആലോചിക്കുന്നു…" },
    pickLanguage: "ഏത് ഭാഷയിൽ സംസാരിക്കാനാണ് ആഗ്രഹം?",
  },
  mr: {
    greeting: "नमस्कार, मी सिम्मी, सिम्पलची AI मार्गदर्शक. तुमच्या व्यवसायासाठी AI कशी उपयोगी पडू शकते, याबद्दल काहीही विचारा.",
    voiceGreeting: "नमस्कार! मी सिम्मी, सिम्पलची AI मार्गदर्शक. तुमच्या व्यवसायात AI ने कशात मदत करावी असे तुम्हाला वाटते?",
    clarify: "माफ करा, ते स्पष्ट ऐकू आले नाही. कृपया पुन्हा एकदा सांगाल का?",
    nudge: "तुम्ही अजून आहात का? हवे असल्यास, सिम्पल AI ला खऱ्या व्यवसाय परिणामांमध्ये कसे बदलते ते सोप्या भाषेत सांगते.",
    nudgeMeeting: "तुम्ही अजून आहात का? हवे असल्यास, आमच्या AI strategist सोबत एक छोटा call ठरवते.",
    end: "बराच वेळ बोलणे झाले नाही, म्हणून हे session बंद करत आहे. तयार असाल तेव्हा पुन्हा बोला.",
    micDenied: "बोलण्यासाठी मला microphone ची परवानगी हवी. Browser मध्ये परवानगी द्या, किंवा खाली type करा.",
    listening: "मी ऐकत आहे. तुम्हाला काय जाणून घ्यायचे आहे?",
    starters: ["माझ्यासाठी AI च्या संधी शोधा", "Cognitive AI म्हणजे काय?", "तुम्ही कसे काम करता?", "AI strategist शी बोलायचे आहे"],
    askFollowUp: "पुढचा प्रश्न विचारा",
    askAnything: "सिम्मीला काहीही विचारा…",
    status: { thinking: "विचार करत आहे…" },
    pickLanguage: "तुम्हाला कोणत्या भाषेत बोलायला आवडेल?",
  },
  bn: {
    greeting: "নমস্কার, আমি সিম্মি, সিম্পেলের AI গাইড। আপনার ব্যবসায় AI কীভাবে কাজে আসতে পারে, যা খুশি জিজ্ঞেস করুন।",
    voiceGreeting: "নমস্কার! আমি সিম্মি, সিম্পেলের AI গাইড। আপনার ব্যবসায় AI কোন কাজে সাহায্য করুক বলে আপনি চান?",
    clarify: "দুঃখিত, সেটা পরিষ্কার শোনা গেল না। আরেকবার বলবেন কি?",
    nudge: "আপনি কি এখনও আছেন? চাইলে, সিম্পেল কীভাবে AI-কে আসল ব্যবসায়িক ফলাফলে বদলে দেয়, সহজ করে বলতে পারি।",
    nudgeMeeting: "আপনি কি এখনও আছেন? চাইলে, আমাদের একজন AI strategist-এর সঙ্গে একটা ছোট call ঠিক করে দিতে পারি।",
    end: "অনেকক্ষণ কথা হয়নি, তাই এই session শেষ করছি। প্রস্তুত হলে আবার কথা বলবেন।",
    micDenied: "কথা বলার জন্য আমার microphone-এর অনুমতি দরকার। Browser-এ অনুমতি দিন, অথবা নিচে type করুন।",
    listening: "আমি শুনছি। কী জানতে চান?",
    starters: ["আমার জন্য AI-এর সুযোগ খুঁজুন", "Cognitive AI কী?", "আপনারা কীভাবে কাজ করেন?", "AI strategist-এর সঙ্গে কথা বলতে চাই"],
    askFollowUp: "পরের প্রশ্ন করুন",
    askAnything: "সিম্মিকে যা খুশি জিজ্ঞেস করুন…",
    status: { thinking: "ভাবছি…" },
    pickLanguage: "আপনি কোন ভাষায় কথা বলতে চান?",
  },
  gu: {
    greeting: "નમસ્તે, હું સિમ્મી છું, સિમ્પલની AI માર્ગદર્શક. તમારા વ્યવસાયમાં AI કેવી રીતે કામ આવી શકે, એ વિશે કંઈ પણ પૂછો.",
    voiceGreeting: "નમસ્તે! હું સિમ્મી છું, સિમ્પલની AI માર્ગદર્શક. તમારા વ્યવસાયમાં AI શેમાં મદદ કરે એવું તમે ઇચ્છો છો?",
    clarify: "માફ કરશો, એ સ્પષ્ટ સંભળાયું નહીં. ફરી એક વાર કહેશો?",
    nudge: "તમે હજી છો? ઇચ્છો તો, સિમ્પલ AI ને સાચા વ્યવસાયિક પરિણામોમાં કેવી રીતે બદલે છે તે સરળ રીતે કહું.",
    nudgeMeeting: "તમે હજી છો? ઇચ્છો તો, અમારા AI strategist સાથે એક નાનો call ગોઠવી આપું.",
    end: "ઘણા સમયથી વાત થઈ નથી, તેથી આ session બંધ કરું છું. તૈયાર હો ત્યારે ફરી વાત કરજો.",
    micDenied: "વાત કરવા માટે મને microphone ની પરવાનગી જોઈએ. Browser માં પરવાનગી આપો, અથવા નીચે type કરો.",
    listening: "હું સાંભળું છું. તમે શું જાણવા માંગો છો?",
    starters: ["મારા માટે AI ની તકો શોધો", "Cognitive AI શું છે?", "તમે કેવી રીતે કામ કરો છો?", "AI strategist સાથે વાત કરવી છે"],
    askFollowUp: "આગળનો પ્રશ્ન પૂછો",
    askAnything: "સિમ્મીને કંઈ પણ પૂછો…",
    status: { thinking: "વિચારું છું…" },
    pickLanguage: "તમે કઈ ભાષામાં વાત કરવા માંગો છો?",
  },
  pa: {
    greeting: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ, ਮੈਂ ਸਿੰਮੀ ਹਾਂ, ਸਿੰਪਲ ਦੀ AI ਗਾਈਡ। ਤੁਹਾਡੇ ਕਾਰੋਬਾਰ ਵਿੱਚ AI ਕਿਵੇਂ ਕੰਮ ਆ ਸਕਦੀ ਹੈ, ਕੁਝ ਵੀ ਪੁੱਛੋ।",
    voiceGreeting: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ ਸਿੰਮੀ ਹਾਂ, ਸਿੰਪਲ ਦੀ AI ਗਾਈਡ। ਤੁਸੀਂ ਆਪਣੇ ਕਾਰੋਬਾਰ ਵਿੱਚ AI ਤੋਂ ਕਿਸ ਚੀਜ਼ ਵਿੱਚ ਮਦਦ ਚਾਹੁੰਦੇ ਹੋ?",
    clarify: "ਮਾਫ਼ ਕਰਨਾ, ਇਹ ਸਾਫ਼ ਸੁਣਾਈ ਨਹੀਂ ਦਿੱਤਾ। ਕੀ ਤੁਸੀਂ ਇੱਕ ਵਾਰ ਫਿਰ ਦੱਸ ਸਕਦੇ ਹੋ?",
    nudge: "ਕੀ ਤੁਸੀਂ ਹਾਲੇ ਵੀ ਇੱਥੇ ਹੋ? ਚਾਹੋ ਤਾਂ ਮੈਂ ਸੌਖੇ ਸ਼ਬਦਾਂ ਵਿੱਚ ਦੱਸ ਸਕਦੀ ਹਾਂ ਕਿ ਸਿੰਪਲ AI ਨੂੰ ਅਸਲ ਕਾਰੋਬਾਰੀ ਨਤੀਜਿਆਂ ਵਿੱਚ ਕਿਵੇਂ ਬਦਲਦਾ ਹੈ।",
    nudgeMeeting: "ਕੀ ਤੁਸੀਂ ਹਾਲੇ ਵੀ ਇੱਥੇ ਹੋ? ਚਾਹੋ ਤਾਂ ਮੈਂ ਸਾਡੇ AI strategist ਨਾਲ ਇੱਕ ਛੋਟੀ call ਤੈਅ ਕਰ ਦਿੰਦੀ ਹਾਂ।",
    end: "ਕਾਫ਼ੀ ਦੇਰ ਤੋਂ ਗੱਲ ਨਹੀਂ ਹੋਈ, ਇਸ ਲਈ ਮੈਂ ਇਹ session ਬੰਦ ਕਰ ਰਹੀ ਹਾਂ। ਜਦੋਂ ਤਿਆਰ ਹੋਵੋ, ਫਿਰ ਗੱਲ ਕਰਨਾ।",
    micDenied: "ਗੱਲ ਕਰਨ ਲਈ ਮੈਨੂੰ microphone ਦੀ ਇਜਾਜ਼ਤ ਚਾਹੀਦੀ ਹੈ। Browser ਵਿੱਚ ਇਜਾਜ਼ਤ ਦਿਓ, ਜਾਂ ਹੇਠਾਂ type ਕਰੋ।",
    listening: "ਮੈਂ ਸੁਣ ਰਹੀ ਹਾਂ। ਤੁਸੀਂ ਕੀ ਜਾਣਨਾ ਚਾਹੋਗੇ?",
    starters: ["ਮੇਰੇ ਲਈ AI ਦੇ ਮੌਕੇ ਲੱਭੋ", "Cognitive AI ਕੀ ਹੈ?", "ਤੁਸੀਂ ਕਿਵੇਂ ਕੰਮ ਕਰਦੇ ਹੋ?", "AI strategist ਨਾਲ ਗੱਲ ਕਰਨੀ ਹੈ"],
    askFollowUp: "ਅਗਲਾ ਸਵਾਲ ਪੁੱਛੋ",
    askAnything: "ਸਿੰਮੀ ਨੂੰ ਕੁਝ ਵੀ ਪੁੱਛੋ…",
    status: { thinking: "ਸੋਚ ਰਹੀ ਹਾਂ…" },
    pickLanguage: "ਤੁਸੀਂ ਕਿਸ ਭਾਸ਼ਾ ਵਿੱਚ ਗੱਲ ਕਰਨਾ ਚਾਹੋਗੇ?",
  },
  or: {
    greeting: "ନମସ୍କାର, ମୁଁ ସିମ୍ମି, ସିମ୍ପେଲର AI ଗାଇଡ୍। ଆପଣଙ୍କ ବ୍ୟବସାୟରେ AI କିପରି କାମରେ ଆସିପାରେ, ଯାହା ଇଚ୍ଛା ପଚାରନ୍ତୁ।",
    voiceGreeting: "ନମସ୍କାର! ମୁଁ ସିମ୍ମି, ସିମ୍ପେଲର AI ଗାଇଡ୍। ଆପଣଙ୍କ ବ୍ୟବସାୟରେ AI କେଉଁଥିରେ ସାହାଯ୍ୟ କରୁ ବୋଲି ଆପଣ ଚାହାଁନ୍ତି?",
    clarify: "କ୍ଷମା କରନ୍ତୁ, ତାହା ସ୍ପଷ୍ଟ ଶୁଣାଗଲା ନାହିଁ। ଆଉ ଥରେ କହିବେ କି?",
    nudge: "ଆପଣ ଏବେ ବି ଅଛନ୍ତି କି? ଚାହିଁଲେ, ସିମ୍ପେଲ କିପରି AI କୁ ପ୍ରକୃତ ବ୍ୟବସାୟିକ ଫଳାଫଳରେ ବଦଳାଏ ତାହା ସହଜରେ କହିପାରିବି।",
    nudgeMeeting: "ଆପଣ ଏବେ ବି ଅଛନ୍ତି କି? ଚାହିଁଲେ, ଆମର ଜଣେ AI strategist ଙ୍କ ସହ ଏକ ଛୋଟ call ଠିକ୍ କରିଦେବି।",
    end: "ବହୁତ ସମୟ ହେଲା କଥା ହୋଇନାହିଁ, ତେଣୁ ଏହି session ବନ୍ଦ କରୁଛି। ପ୍ରସ୍ତୁତ ହେଲେ ପୁଣି କଥା ହେବେ।",
    micDenied: "କଥା ହେବା ପାଇଁ ମୋତେ microphone ଅନୁମତି ଦରକାର। Browser ରେ ଅନୁମତି ଦିଅନ୍ତୁ, କିମ୍ବା ତଳେ type କରନ୍ତୁ।",
    listening: "ମୁଁ ଶୁଣୁଛି। ଆପଣ କଣ ଜାଣିବାକୁ ଚାହାଁନ୍ତି?",
    starters: ["ମୋ ପାଇଁ AI ସୁଯୋଗ ଖୋଜନ୍ତୁ", "Cognitive AI କଣ?", "ଆପଣ କିପରି କାମ କରନ୍ତି?", "AI strategist ଙ୍କ ସହ କଥା ହେବି"],
    askFollowUp: "ପରବର୍ତ୍ତୀ ପ୍ରଶ୍ନ ପଚାରନ୍ତୁ",
    askAnything: "ସିମ୍ମିଙ୍କୁ ଯାହା ଇଚ୍ଛା ପଚାରନ୍ତୁ…",
    status: { thinking: "ଭାବୁଛି…" },
    pickLanguage: "ଆପଣ କେଉଁ ଭାଷାରେ କଥା ହେବାକୁ ଚାହାଁନ୍ତି?",
  },
};
for (const [code, text] of Object.entries(MORE_TEXT)) {
  TEXT[code] = { ...TEXT.en, ...text, status: { ...TEXT.en.status, ...text.status } };
}

// The picker: each language by its own name, a short label for the button, and the speech-recognition locale.
export const LANGUAGE_INFO = {
  en: { name: "English", short: "EN", speech: "en-IN" },
  hi: { name: "हिन्दी", short: "हिं", speech: "hi-IN" },
  ta: { name: "தமிழ்", short: "த", speech: "ta-IN" },
  te: { name: "తెలుగు", short: "తె", speech: "te-IN" },
  kn: { name: "ಕನ್ನಡ", short: "ಕ", speech: "kn-IN" },
  ml: { name: "മലയാളം", short: "മ", speech: "ml-IN" },
  mr: { name: "मराठी", short: "मरा", speech: "mr-IN" },
  bn: { name: "বাংলা", short: "বা", speech: "bn-IN" },
  gu: { name: "ગુજરાતી", short: "ગુ", speech: "gu-IN" },
  pa: { name: "ਪੰਜਾਬੀ", short: "ਪੰ", speech: "pa-IN" },
  or: { name: "ଓଡ଼ିଆ", short: "ଓ", speech: "or-IN" },
};

// The locale speech recognition listens in. English is always en-IN: it understands other English accents too, and
// Hindi spoken to it comes back as romanized Hindi ("mujhe AI ke baare mein batao"), which detectLanguage catches.
// en-US turns the same speech into garbled English, so Simmi would answer a Hindi speaker in English.
export const speechLang = (lang) => LANGUAGE_INFO[lang]?.speech || "en-IN";

export const greetingMessage = (lang) => ({ id: "greeting", role: "assistant", text: TEXT[lang].greeting, actions: [] });
export const GREETING = greetingMessage("en");
export const STARTER_PROMPTS = TEXT.en.starters;

// Each Indian script belongs to one language (Devanagari: Hindi, or Marathi by its words). Mirrors the backend.
const SCRIPTS = [
  [/[஀-௿]/, "ta"],
  [/[ఀ-౿]/, "te"],
  [/[ಀ-೿]/, "kn"],
  [/[ഀ-ൿ]/, "ml"],
  [/[ঀ-৿]/, "bn"],
  [/[઀-૿]/, "gu"],
  [/[਀-੿]/, "pa"],
  [/[଀-୿]/, "or"],
];
const MARATHI_WORDS = new Set(
  ("आहे आहेत नाही मला तुम्ही तुमच्या तुमचा तुमची काय कसे कसा आम्ही आमच्या आमचा माझा माझी माझ्या हवे हवा करू शकता " +
    "शकतो मध्ये आणि सांगा कशी होईल पाहिजे बद्दल व्यवसाय").split(" "),
);

// Hinglish: several common Hindi words in English letters.
const HINGLISH_WORDS = new Set(
  ("kya hai hain kaise kaisa kaisi kitna kitne kitni mujhe mera meri mere aap aapka aapki aapke hamara humara hamare " +
    "nahi nahin haan batao bataiye chahiye karna karte karta karti sakte sakta sakti hoga hogi hota kahan kab kyun " +
    "kaun kuch bahut accha acha theek thik samjhao madad liye wala wali mein ke ka ki ko aur yeh woh abhi matlab " +
    "shukriya dhanyavaad namaste namaskar ho hu hoon bana banana banao milega milegi milta karo kare karein kijiye " +
    "dijiye raha rahi gaya tha thi jaise wahan yahan lagega lagta hamein").split(" "),
);
const ENGLISH_WORDS = new Set(
  "the is are what how do does you your can my we our for of and with this that it about which where when why who tell have".split(" "),
);
// Common English words as Hindi speech recognition spells them when someone speaks English ("व्हाट इज़ प्राइसिंग").
// ...and back to English: when Simmi knows the visitor spoke English but only has the Hindi-script spelling, these
// words are turned back into English so she can understand the question (see toEnglishWords).
const DEVANAGARI_ENGLISH = {
  व्हाट: "what", वॉट: "what", इज़: "is", इज: "is", आर: "are", डू: "do", डज़: "does", यू: "you", योर: "your",
  यौर: "your", कैन: "can", माय: "my", वी: "we", अवर: "our", फॉर: "for", फ़ॉर: "for", ऑफ: "of", एंड: "and", एन्ड: "and",
  विद: "with", दिस: "this", दैट: "that", इट: "it", अबाउट: "about", व्हिच: "which", वेयर: "where", व्हेयर: "where",
  वेन: "when", व्हेन: "when", व्हाई: "why", हू: "who", हाउ: "how", टेल: "tell", हैव: "have", आइ: "I", मी: "me",
  ए: "a", एन: "an", टू: "to", ऑन: "on", एट: "at", प्लीज़: "please", प्लीज: "please", वांट: "want", नीड: "need",
  नो: "know", लाइक: "like", वुड: "would", कुड: "could", शुड: "should", विल: "will", गेट: "get", गिव: "give",
  शो: "show", एक्सप्लेन: "explain", मोर: "more", मच: "much", मेनी: "many", सम: "some", एनी: "any", देयर: "there",
  देम: "them", ओके: "okay", ओकै: "okay", थैंक्स: "thanks", थैंक: "thank", यस: "yes", श्योर: "sure", द: "the",
  आउट: "out", नाउ: "now", डूइंग: "doing", एक्जेक्टली: "exactly", एग्जैक्टली: "exactly", समथिंग: "something",
  हियर: "hear", मेड: "made", बिल्ट: "built", हेलो: "hello", हाय: "hi", बाय: "bye", गुड: "good", ग्रेट: "great",
  प्राइस: "price", प्राइसिंग: "pricing", कॉस्ट: "cost", सर्विस: "service", सर्विसेज: "services", सर्विसेस: "services",
  प्रोडक्ट: "product", प्रोडक्ट्स: "products", कंपनी: "company", बिज़नेस: "business", बिजनेस: "business",
  हेल्प: "help", वर्क: "work", ऑफर: "offer", कॉग्निटिव: "cognitive", कॉग्नेटिव: "cognitive", एआई: "AI",
  डेमो: "demo", मीटिंग: "meeting", कॉल: "call", ऑटोमेशन: "automation", ईआरपी: "ERP", इंटीग्रेशन: "integration",
  व्हाट्सएप: "whatsapp", व्हाट्सऐप: "whatsapp", बॉट: "bot", इंडस्ट्री: "industry", इंडस्ट्रीज: "industries",
  हेल्थकेयर: "healthcare", मैन्युफैक्चरिंग: "manufacturing", सिक्योरिटी: "security", डेटा: "data", डाटा: "data",
  सेफ: "safe", कॉन्टैक्ट: "contact", ऑफिस: "office", जॉब: "job", जॉब्स: "jobs", हायरिंग: "hiring", लैब्स: "labs",
  सिम्मी: "Simmi", सिम्पल: "Simpel", एजेंट: "agent", एजेंट्स: "agents", प्लेटफॉर्म: "platform", सॉल्यूशन: "solution",
  सॉल्यूशंस: "solutions", कस्टमर: "customer", कस्टमर्स: "customers", फाउंडर: "founder", सीईओ: "CEO",
  लोकेशन: "location", एड्रेस: "address", लोन: "loan", पेरोल: "payroll", ऑपर्चुनिटीज: "opportunities",
};
// आई is "I" or "AI": right after a word like cognitive or before business it is AI (handled in toEnglishWords).
const ENGLISH_IN_DEVANAGARI = new Set([...Object.keys(DEVANAGARI_ENGLISH), "आई"]);
// English spoken to Hindi recognition, turned back into English words (unknown words stay as they are).
export function toEnglishWords(text) {
  const words = text.split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const bare = w.replace(/[।?!.,]/g, "");
      if (bare === "आई") {
        const near = `${words[i - 1] || ""} ${words[i + 1] || ""}`;
        return /कॉग्नि|कॉग्ने|बिज़नेस|बिजनेस|एजेंट|प्लेटफॉर्म|सॉल्यूशन|प्राइवेट/.test(near) ? "AI" : "I";
      }
      return DEVANAGARI_ENGLISH[bare] ? w.replace(bare, DEVANAGARI_ENGLISH[bare]) : w;
    })
    .join(" ");
}
// Which language the visitor spoke, from the browser's hi-IN transcript: Hindi speech comes back in Hindi script,
// English mostly in English letters, or as English words spelled in Hindi script.
export function spokenLanguage(text) {
  const hindiWords = text.match(/[\u0900-\u097F]+/g) || [];
  const latinWords = text.match(/[a-z']+/gi) || [];
  if (!hindiWords.length) return latinWords.length ? (detectLanguage(text) === "hi" ? "hi" : "en") : null;
  const englishSpelled = hindiWords.filter((w) => ENGLISH_IN_DEVANAGARI.has(w)).length;
  // Mostly English words (in either script): English. Otherwise Hindi (Hinglish keeps some English words).
  return (englishSpelled + latinWords.length) / (hindiWords.length + latinWords.length) >= 0.6 ? "en" : "hi";
}
// The language a message is clearly in, else null (keep the current one). Romanized Tamil etc. is left to the
// backend (Claude switches with its set_language tool).
export function detectLanguage(text) {
  for (const [pattern, code] of SCRIPTS) if (pattern.test(text)) return code;
  if (/[\u0900-\u097F]/.test(text)) {
    const words = text.match(/[\u0900-\u097F]+/g) || [];
    return words.filter((w) => MARATHI_WORDS.has(w)).length >= 2 ? "mr" : "hi";
  }
  const words = text.toLowerCase().match(/[a-z']+/g) || [];
  const hindi = new Set(words.filter((w) => HINGLISH_WORDS.has(w)));
  if (hindi.size >= 2) return "hi";
  if (words.length <= 3 && words.some((w) => ["namaste", "namaskar", "shukriya", "dhanyavaad"].includes(w))) return "hi";
  if (new Set(words.filter((w) => ENGLISH_WORDS.has(w))).size >= 2 && !hindi.size) return "en";
  return null;
}

// Every new conversation starts in English: Simmi greets in English and switches only once the visitor writes or
// speaks another language. A conversation restored after a refresh carries on in the language it was in.
const initialLanguage = (saved) => (LANGUAGES.includes(saved?.lang) ? saved.lang : "en");

export const AVATAR_VIDEO_ID = "aura-avatar-video";
const CAPTION_WORD_MS = 360; // ~165 words a minute, a natural speaking pace
const AVATAR_CONNECT_TIMEOUT_MS = 20000;
// Pause after closing a session before opening the next. Anam allows one live session on the free plan and
// takes a moment to release it; restarting sooner got the new session rejected (Simmi fell back to the browser voice).
const AVATAR_RELEASE_MS = 2500;
const AVATAR_RETRY_MS = 2500; // one more try after a failed connect, before falling back to the browser voice
const AVATAR_QUICK_FAIL_MS = 6000; // only a failure this quick is retried
const AVATAR_WAIT_MS = 8000; // how long a reply waits for a still-connecting avatar before using the browser voice
const TOKEN_FRESH_MS = 60000; // a session token fetched ahead of time (on hover) is used only while this fresh

// Idle timings (ms): check in after a pause, then close the session if the visitor stays quiet.
const IDLE = { text: { nudge: 45000, end: 60000 }, voice: { nudge: 15000, end: 20000 } };

export const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
// Phones: browser speech recognition fights the avatar call for the microphone (and Android beeps on every
// restart), so the call's Hindi listener (see sniffRef) runs on desktop only.
const MOBILE = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

// Browser voices vary a lot in quality. Prefer the neural/"Natural" voices Edge and Chrome ship,
// then well-known good voices, then anything in the visitor's language.
// Edge's online "Natural" voices are dependable. Chrome's online "Google" voices are not: they often stop
// mid-sentence without any event, so in Chrome the voices installed on the computer come first.
const IS_EDGE = typeof navigator !== "undefined" && /Edg\//.test(navigator.userAgent);
const PREFERRED_VOICES = {
  en: IS_EDGE
    ? [/natural/i, /neural/i, /online/i, /heera|aria|jenny|sonia|zira|samantha/i]
    : [/heera|aria|jenny|sonia|zira|samantha|female/i, /natural/i, /neural/i],
  // Female Hindi voices first (Windows: Swara, Kalpana; Edge online: Swara Natural), then any Hindi voice.
  hi: [/swara|kalpana|female/i, /natural/i, /neural/i, /.*/],
};
// Other Indian languages: a natural/neural voice if the browser has one (Edge ships them online), else any.
const ANY_VOICE = [/female/i, /natural/i, /neural/i, /.*/];

function pickVoice(language = "en") {
  const voices = window.speechSynthesis.getVoices();
  const pool = voices.filter((v) => v.lang.toLowerCase().startsWith(language));
  const ordered = IS_EDGE ? pool : [...pool.filter((v) => v.localService), ...pool.filter((v) => !v.localService)];
  for (const pattern of PREFERRED_VOICES[language] || ANY_VOICE) {
    const match = ordered.find((v) => pattern.test(v.name));
    if (match) return match;
  }
  return ordered[0] || null;
}

// Browser speech is spoken a sentence at a time: long utterances are what Chrome most often drops.
// (। is the full stop in Hindi, Marathi, Bengali, Punjabi and Odia.)
const sentencesOf = (text) => text.match(/[^.!?।]+[.!?।]+["')\]]*\s*|[^.!?।]+$/g) || [text];

// Speech-to-text output that's too short or too uncertain to act on.
// Anam drops the first word or two of a persona's greeting from its text ("there! I'm here…" for "Hi there! I'm
// here…"). The greeting as written in Anam Lab is known, so a greeting that arrives without its start gets it back.
// Some LLMs leak their internal markup into the reply ("<think…>", "<|message|>", "<await>"): never show it.
const stripModelTags = (text) => (text || "").replace(/<\|[^|>]*\|>|<\/?(think|await|analysis|final)[^>]*>?/gi, "");

const withGreetingStart = (text, greeting) => {
  const said = (text || "").trimStart();
  if (!greeting || !said) return text;
  const at = greeting.indexOf(said.slice(0, 25));
  return at > 0 && at <= 20 ? greeting.slice(0, at) + said : text;
};

const isUnclear = (transcript, confidence) =>
  transcript.replace(/[^a-z\u0900-\u0D7F]/gi, "").length < 2 || (confidence > 0 && confidence < 0.35);

// Speech-to-text that is really Simmi's own voice coming back through the visitor's speakers (no headphones):
// most of its words are in what she is saying right now.
const wordsOf = (text) => text.toLowerCase().match(/[a-z\u0900-\u0D7F']{3,}/g) || []; // English and Indian scripts
const isEcho = (heard, speaking) => {
  const words = wordsOf(heard);
  if (!speaking || words.length < 3) return false;
  const said = new Set(wordsOf(speaking));
  return words.filter((w) => said.has(w)).length / words.length >= 0.7;
};
// Short phrases ("wait", "stop that") count as her echo only if every word is one she is saying right now.
const isShortEcho = (heard, speaking) => {
  const words = wordsOf(heard);
  if (!speaking || !words.length) return false;
  const said = new Set(wordsOf(speaking));
  return words.every((w) => said.has(w));
};
// Enough of the visitor's own words to stop Simmi mid-answer (a cough or one stray word doesn't).
const BARGE_IN_WORDS = 2;
const SNIFF_FRESH_MS = 5000; // the call's Hindi listener: what it heard counts only this long (see sniffRef)

// The typed conversation is kept in this tab's sessionStorage, so a refresh (or going back to the tab) carries on
// where the visitor left off, in the same backend session. A voice call is never restored, only its transcript.
const SAVED_CHAT = "simmi_chat";
const SAVED_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const SAVED_MAX_MESSAGES = 40;
function loadSavedChat() {
  try {
    const saved = JSON.parse(storage.get("sessionStorage", SAVED_CHAT) || "null");
    if (saved?.v !== 1 || !Array.isArray(saved.messages) || Date.now() - saved.savedAt > SAVED_MAX_AGE_MS) return null;
    return saved;
  } catch {
    return null;
  }
}

// Chat state follows the visitor across pages (single-page app) and survives a refresh (see SAVED_CHAT).
export function AuraProvider({ children }) {
  const location = useLocation();
  const [saved] = useState(loadSavedChat);
  const [sessionId, setSessionId] = useState(saved?.sessionId || null);
  const [lang, setLang] = useState(() => initialLanguage(saved));
  const langRef = useRef(lang);
  const t = TEXT[lang];
  const [messages, setMessages] = useState(() => saved?.messages || [greetingMessage(langRef.current)]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const messageTimesRef = useRef(new Map());
  useEffect(() => {
    for (const m of messages) {
      if (!messageTimesRef.current.has(m.id)) messageTimesRef.current.set(m.id, new Date().toISOString());
    }
  }, [messages]);
  const getConversation = useCallback(
    () =>
      messagesRef.current
        .filter((m) => (m.role === "user" || m.role === "assistant") && (m.text || "").trim())
        .map((m) => ({
          role: m.role,
          text: toSpeech(m.text).trim() || m.text.trim(),
          ts: messageTimesRef.current.get(m.id) || new Date().toISOString(),
        })),
    [],
  );
  const [meetingBooked, setMeetingBooked] = useState(Boolean(saved?.meetingBooked));
  const [leadCaptured, setLeadCaptured] = useState(Boolean(saved?.leadCaptured));
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // what Simmi is doing while a reply is on its way (TEXT.status key)
  // The persona asked the visitor to type something (e.g. their phone number or email): the typing box opens.
  const [typeRequestAt, setTypeRequestAt] = useState(0);
  const personaTurnRef = useRef({ id: null, text: "" }); // what the persona is saying right now, in full
  const [config, setConfig] = useState({ mode: "offline", booking_url: "" });
  const [open, setOpen] = useState(Boolean(saved?.open)); // floating panel
  const [heroVisible, setHeroVisible] = useState(false);
  // Typed chat is spoken too (like Docket): replies are voiced — by the avatar when available — and written
  // out in step with the voice. The visitor can switch the voice off.
  const [voiceOn, setVoiceOn] = useState(true);
  const voiceOnRef = useRef(true);
  voiceOnRef.current = voiceOn;
  const [textVoice, setTextVoice] = useState("off"); // avatar voice for typed chat: off | connecting | on
  const textVoiceWantedRef = useRef(false);
  const [speakingId, setSpeakingId] = useState(null); // typed-chat reply currently being spoken
  const speakingIdRef = useRef(null);
  // Voice conversation ("Talk to Simmi")
  const [callActive, setCallActive] = useState(false);
  const [callState, setCallState] = useState("idle"); // idle | listening | thinking | speaking
  const [caption, setCaption] = useState("");
  const [talked, setTalked] = useState(false); // visitor has started a voice session at least once
  const [reviewing, setReviewing] = useState(false); // (unused by the dock: hanging up hides the agent)
  const [hidden, setHidden] = useState(false); // agent collapsed to the small "Talk to Simmi" button
  // Speaker button: mute Simmi's voice (the conversation keeps going; captions still show).
  const [outputMuted, setOutputMuted] = useState(false);
  const outputMutedRef = useRef(false);
  outputMutedRef.current = outputMuted;
  const [typingAt, setTypingAt] = useState(0); // last keystroke: typing counts as activity for the idle flow
  const noteTyping = useCallback(() => setTypingAt(Date.now()), []);
  const [avatarMode, setAvatarMode] = useState(false); // call is running on the Anam video avatar
  const [callSurface, setCallSurface] = useState("panel"); // where the call is shown: "hero" (frameless) | "panel"
  const [muted, setMuted] = useState(false); // visitor muted their mic during a call
  const mutedRef = useRef(false);

  const voiceRef = useRef(null);
  // Anam avatar session (same provider as Docket's Aura). Anam hears the visitor and speaks;
  // Simmi's backend decides what to say.
  const anamRef = useRef(null);
  const pendingEndRef = useRef(false);
  const handledUserMsgRef = useRef(new Set());
  const speakTimerRef = useRef(null);
  const sayRef = useRef(() => {});
  const openingRef = useRef(null); // Simmi's spoken opening line for this session
  const callRef = useRef(false);
  const recognitionRef = useRef(null);
  const sessionRef = useRef(saved?.sessionId || null);
  const sendingRef = useRef(false);
  const listenRef = useRef(() => {});
  const sendRef = useRef(() => {});
  const finishRef = useRef(() => {}); // ends the conversation for good (hang up + fresh start next time)
  const queuedUtteranceRef = useRef(""); // typed chat: a message sent while Simmi was still answering
  // Barge-in: each reply is a "turn"; interrupting starts a new one and abandons the old (its request is aborted).
  const turnRef = useRef(0);
  const turnAbortRef = useRef(null);
  const saying = useRef(""); // what Simmi is saying right now (to recognise her own voice coming back as echo)
  const activeTalkIdRef = useRef(null); // the avatar talk stream now playing; events from older ones are ignored
  const heardRef = useRef(""); // the visitor's live words from the avatar's speech-to-text
  const bargeInRef = useRef(() => false);
  // The Anam Lab persona answers (config.persona_brain): Anam hears the visitor, its LLM replies with the persona's
  // prompt and greeting, and Simmi only shows the conversation. Typed messages go to the persona too.
  const personaBrainRef = useRef(false);
  const typedToPersonaRef = useRef([]); // typed messages sent to the persona (so its history doesn't show them twice)
  // The persona often sends one turn as several messages ("Hi there!" then "I'm here to help…"). They are shown as one
  // bubble: each later message id maps to the bubble it joined.
  const personaBubbleRef = useRef(new Map());
  const personaGreetingRef = useRef(""); // the persona's greeting as written in Anam Lab (see withGreetingStart)
  const personaHasSpokenRef = useRef(false); // the persona finished its first sentence (its greeting) this session
  // A call started by "Book a Meeting": the persona's general greeting is stopped and hidden, and the visitor's
  // request is answered straight away. personaGreetingIdRef is the greeting's message id (to hide it).
  const skipGreetingRef = useRef(false);
  const bookingCallRef = useRef(false); // requestMeeting -> startCall: this call is for booking
  const personaGreetingIdRef = useRef(null);
  // The booking request waits here until the persona's greeting has been stopped (sent exactly once).
  const pendingBookingAskRef = useRef(null);
  const bookingStartingRef = useRef(false); // a Book a Meeting click is being handled: repeat clicks are ignored
  const flushBookingAskRef = useRef(() => {});
  const setLanguageRef = useRef(async () => {});
  const callGenRef = useRef(0); // each started call; a slow, superseded start gives up instead of greeting twice

  useEffect(() => {
    auraApi.config().then(setConfig).catch(() => {});
  }, []);

  // The Anam Lab persona's greeting, to restore the start Anam leaves out of its text (see withGreetingStart).
  useEffect(() => {
    if (!config.persona_brain) return;
    auraApi
      .avatarPreview()
      .then((preview) => {
        personaGreetingRef.current = preview?.greeting || "";
      })
      .catch(() => {});
  }, [config.persona_brain]);

  // Voices load asynchronously in Chrome; pick once they're available.
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;
    const update = () => {
      voiceRef.current = pickVoice(langRef.current);
    };
    update();
    synth.addEventListener?.("voiceschanged", update);
    return () => synth.removeEventListener?.("voiceschanged", update);
  }, []);

  // Speaks a reply with the browser voice, sentence by sentence. A watchdog moves on if the browser stalls
  // (Chrome sometimes stops without an end event), so Simmi never freezes mid-reply.
  const speechRunRef = useRef(0);
  const speak = useCallback(
    (text, { force = false, onEnd, onWord } = {}) => {
      const synth = window.speechSynthesis;
      if (!synth || (!force && !voiceOn)) {
        onEnd?.();
        return;
      }
      const run = ++speechRunRef.current; // a newer speak() supersedes this one
      synth.cancel();
      const parts = sentencesOf(text);
      let offset = 0;
      let finished = false;
      const finish = () => {
        if (finished || speechRunRef.current !== run) return;
        finished = true;
        onEnd?.();
      };
      const sayPart = (i) => {
        if (speechRunRef.current !== run) return;
        if (i >= parts.length) {
          finish();
          return;
        }
        const part = parts[i];
        const start = offset;
        offset += part.length;
        const utterance = new SpeechSynthesisUtterance(part.replace(/[*_#•]/g, ""));
        if (voiceRef.current) {
          utterance.voice = voiceRef.current;
          utterance.lang = voiceRef.current.lang;
        } else if (langRef.current !== "en") {
          utterance.lang = speechLang(langRef.current); // no voice picked for it: let the browser choose any it has
        }
        utterance.rate = 1.02;
        utterance.volume = outputMutedRef.current ? 0 : 1; // muted: keeps the timing, so captions still pace
        let done = false;
        const next = () => {
          if (done) return;
          done = true;
          clearTimeout(watchdog);
          onWord?.(start + part.length);
          sayPart(i + 1);
        };
        // ~2.6 words a second, plus slack for the voice to start.
        const watchdog = setTimeout(() => {
          console.warn("Simmi: browser voice stalled, moving on"); // eslint-disable-line no-console
          synth.cancel();
          next();
        }, 4000 + part.split(/\s+/).length * 600);
        utterance.onend = next;
        utterance.onerror = next;
        // Word boundaries (reported by most local voices) let captions track the exact word being spoken.
        utterance.onboundary = (e) => {
          if (e.name === "word") onWord?.(start + e.charIndex + (e.charLength || 0));
        };
        synth.speak(utterance);
      };
      sayPart(0);
    },
    [voiceOn],
  );

  // Word-by-word captions: the speech card reveals Simmi's words in step with her voice rather than all at
  // once. It advances at a natural speaking pace; the browser voice's word boundaries, when the voice
  // reports them, pin it to the exact word being spoken; end of speech shows the rest.
  const capRef = useRef({ target: "", shown: 0, timer: null, synced: false });
  const stopCaption = useCallback(() => {
    clearInterval(capRef.current.timer);
    capRef.current.timer = null;
  }, []);
  const showCaption = useCallback(
    (text) => {
      stopCaption();
      Object.assign(capRef.current, { target: text, shown: text.length, synced: false });
      setCaption(text);
    },
    [stopCaption],
  );
  const followCaption = useCallback(
    (text, restart = false) => {
      const c = capRef.current;
      if (restart) {
        stopCaption();
        Object.assign(c, { shown: 0, synced: false });
        setCaption("");
      }
      c.target = text;
      if (c.synced || c.timer) return;
      c.timer = setInterval(() => {
        const next = c.target.slice(c.shown).match(/^\s*\S+/);
        if (c.synced || !next) {
          stopCaption(); // caught up — resumes when more of the reply arrives
          return;
        }
        c.shown += next[0].length;
        setCaption(c.target.slice(0, c.shown));
      }, CAPTION_WORD_MS);
    },
    [stopCaption],
  );
  const syncCaption = useCallback(
    (spokenUpTo) => {
      const c = capRef.current;
      c.synced = true;
      stopCaption();
      c.shown = Math.max(c.shown, spokenUpTo);
      setCaption(c.target.slice(0, c.shown));
    },
    [stopCaption],
  );

  const stopListening = () => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (rec) {
      rec.onend = null;
      rec.abort();
    }
  };

  // Close an avatar session. The next session waits for this (plus a moment for Anam to release it):
  // plans limit concurrent sessions (1 on the free plan), and an overlapping start fails — which used to
  // drop Simmi to the browser voice with no lip sync.
  const closingRef = useRef(Promise.resolve());
  const closeAvatar = (client) => {
    closingRef.current = client
      .stopStreaming()
      .catch(() => {})
      .then(() => new Promise((r) => setTimeout(r, AVATAR_RELEASE_MS)));
  };

  const endCall = useCallback(() => {
    callRef.current = false;
    callGenRef.current += 1;
    turnRef.current += 1; // an answer still on its way is dropped
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    sendingRef.current = false;
    setSending(false);
    setStatus(null);
    saying.current = "";
    stopListening();
    speechRunRef.current += 1; // stops the rest of a sentence-by-sentence reply too
    window.speechSynthesis?.cancel();
    clearTimeout(speakTimerRef.current);
    pendingEndRef.current = false;
    const anam = anamRef.current;
    anamRef.current = null;
    if (anam) closeAvatar(anam);
    setAvatarMode(false);
    setCallActive(false);
    setCallState("idle");
    speakingIdRef.current = null;
    setSpeakingId(null);
    showCaption("");
    mutedRef.current = false;
    setMuted(false);
  }, [showCaption]);

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    if (anamRef.current) {
      if (next) anamRef.current.muteInputAudio();
      else anamRef.current.unmuteInputAudio();
    } else if (next) {
      stopListening();
    } else if (callRef.current) {
      listenRef.current();
    }
  }, []);

  // The avatar, if it is connected and ready to speak (a client that is still connecting refuses talk).
  const readyAvatar = () => (anamRef.current?.auraReady ? anamRef.current : null);

  // The avatar for the next reply. If it's still connecting, wait for it (up to AVATAR_WAIT_MS) so the reply
  // is spoken by the avatar with lip sync, instead of by the browser voice over the still idle video.
  const avatarConnectingRef = useRef(null);
  const avatarWhenReady = useCallback(async () => {
    const ready = anamRef.current?.auraReady ? anamRef.current : null;
    if (ready || !avatarConnectingRef.current) return ready;
    await Promise.race([avatarConnectingRef.current, new Promise((r) => setTimeout(r, AVATAR_WAIT_MS))]);
    return anamRef.current?.auraReady ? anamRef.current : null;
  }, []);

  // A spoken typed-chat reply finished: show it in full.
  const textSpeechDone = useCallback(() => {
    clearTimeout(speakTimerRef.current);
    if (!speakingIdRef.current) return;
    showCaption(capRef.current.target);
    speakingIdRef.current = null;
    setSpeakingId(null);
  }, [showCaption]);

  // Typed chat: speak a reply (avatar if connected, else the browser voice) and write it in step.
  const voiceText = useCallback(
    (reply, id) => {
      const text = toSpeech(reply); // her voice and the caption skip the markdown
      speakingIdRef.current = id;
      setSpeakingId(id);
      setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, voiced: true } : m)));
      followCaption(text, true);
      const browserVoice = () => speak(text, { force: true, onWord: syncCaption, onEnd: textSpeechDone });
      avatarWhenReady().then((client) => {
        if (speakingIdRef.current !== id) return; // stopped or replaced while the avatar was connecting
        if (client && !callRef.current) {
          client.talk(text).catch(browserVoice);
          clearTimeout(speakTimerRef.current);
          speakTimerRef.current = setTimeout(textSpeechDone, 3000 + text.split(/\s+/).length * 450);
        } else {
          browserVoice();
        }
      });
    },
    [avatarWhenReady, followCaption, speak, syncCaption, textSpeechDone],
  );

  // The reply with this id is being spoken: the conversation shows it word by word from the caption.
  const markSpeaking = useCallback((id) => {
    if (!id) return;
    speakingIdRef.current = id;
    setSpeakingId(id);
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, voiced: true } : m)));
  }, []);
  // A call reply finished: the conversation shows it in full, and the caption is free for the visitor's words.
  const callSpeechDone = useCallback(() => {
    speakingIdRef.current = null;
    setSpeakingId(null);
    showCaption("");
  }, [showCaption]);

  // The avatar finished (or was interrupted mid-) sentence.
  const avatarDoneSpeaking = useCallback(() => {
    clearTimeout(speakTimerRef.current);
    if (!callRef.current) {
      textSpeechDone();
      return;
    }
    if (pendingEndRef.current) {
      finishRef.current();
      return;
    }
    callSpeechDone();
    setCallState("listening");
  }, [textSpeechDone, callSpeechDone]);

  // The visitor started talking while Simmi was speaking (or still thinking): stop her at once, keep what she
  // had said so far (marked as cut off), and drop the rest of that answer so the new question is answered next.
  const bargeIn = useCallback(() => {
    if (!callRef.current || !(speakingIdRef.current || sendingRef.current)) return false;
    const client = anamRef.current;
    if (client?.auraReady) {
      try {
        client.interruptPersona();
      } catch {
        /* nothing playing */
      }
    }
    activeTalkIdRef.current = "interrupted"; // late events of the stopped reply must not end the next one
    speechRunRef.current += 1;
    window.speechSynthesis?.cancel();
    clearTimeout(speakTimerRef.current);
    pendingEndRef.current = false;
    turnRef.current += 1;
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    sendingRef.current = false;
    setSending(false);
    setStatus(null);
    const cut = speakingIdRef.current;
    if (cut) {
      const c = capRef.current;
      const said = c.target.slice(0, c.shown).trim();
      setMessages((ms) =>
        said ? ms.map((m) => (m.id === cut ? { ...m, text: `${said}…`, cut: true } : m)) : ms.filter((m) => m.id !== cut),
      );
    }
    speakingIdRef.current = null;
    setSpeakingId(null);
    saying.current = "";
    showCaption("");
    setCallState("listening");
    return true;
  }, [showCaption]);
  bargeInRef.current = bargeIn;

  // In a call every Simmi line is spoken, then Simmi listens again (except when it's ending the session).
  // In typed chat, lines are spoken too while the voice is on.
  const say = useCallback(
    (reply, after = "listen", id = null) => {
      if (!callRef.current) {
        if (voiceOnRef.current && id) voiceText(reply, id);
        return;
      }
      const text = toSpeech(reply); // her voice and the caption skip the markdown
      saying.current = text;
      const client = readyAvatar();
      if (client) {
        activeTalkIdRef.current = null; // a plain talk() line: any end-of-speech event is hers
        pendingEndRef.current = after === "end";
        setCallState("speaking");
        markSpeaking(id);
        followCaption(text, true);
        client.talk(text).catch((err) => {
          // Avatar connection dropped mid-conversation: carry on with the browser voice instead of failing.
          console.warn("Simmi: avatar talk failed, falling back to browser voice", err); // eslint-disable-line no-console
          if (anamRef.current !== client) return;
          anamRef.current = null;
          setAvatarMode(false);
          closeAvatar(client);
          clearTimeout(speakTimerRef.current);
          if (callRef.current) sayRef.current(text, after, id);
        });
        // Safety net in case the end-of-speech event never arrives (~2.6 words/sec).
        clearTimeout(speakTimerRef.current);
        speakTimerRef.current = setTimeout(avatarDoneSpeaking, 3000 + text.split(/\s+/).length * 450);
        return;
      }
      setCallState("speaking");
      markSpeaking(id);
      followCaption(text, true);
      speak(text, {
        force: true,
        onWord: syncCaption,
        onEnd: () => {
          if (!callRef.current || speakingIdRef.current !== id) return; // interrupted: the visitor has the floor
          if (after === "end") {
            finishRef.current();
            return;
          }
          callSpeechDone();
          saying.current = "";
          if (recognitionRef.current) setCallState("listening");
          else listenRef.current();
        },
      });
      // Keep listening while she speaks, so the visitor can interrupt her (like a real conversation).
      if (after !== "end" && !recognitionRef.current) listenRef.current(true);
    },
    [speak, avatarDoneSpeaking, followCaption, syncCaption, voiceText, markSpeaking, callSpeechDone],
  );
  sayRef.current = say;

  const addAssistant = useCallback(
    (text, kind, after, actions = []) => {
      const id = randomId();
      setMessages((m) => [...m, { id, role: "assistant", text, actions, kind }]);
      say(text, after, id);
    },
    [say],
  );

  // Speech that didn't come through. Twice in a row usually means the visitor is speaking a language other than
  // the one Simmi is listening in (Anam hears one language at a time), so she offers the language picker.
  const misheardRef = useRef(0);
  const clarify = useCallback(() => {
    misheardRef.current += 1;
    const t = TEXT[langRef.current];
    if (misheardRef.current >= 2) addAssistant(`${t.clarify} ${t.pickLanguage}`, "clarify", "listen", [{ type: "language_picker" }]);
    else addAssistant(t.clarify, "clarify");
  }, [addAssistant]);

  personaBrainRef.current = Boolean(config.persona_brain);

  const send = useCallback(
    // finder: the AI Opportunity Finder's answers ({industry, focus, team_size}) sent along with this message.
    async (raw, { spoken = false, finder = null, language = null } = {}) => {
      const text = raw.trim();
      if (!text) return;
      // The Anam Lab persona answers: the message goes to it, and its reply streams into the conversation.
      if (personaBrainRef.current && !finder) {
        // Wait for the persona (up to 20 s while the avatar connects) rather than letting the backend answer:
        // the visitor should only ever get the persona's flow (e.g. booking one question at a time, not a form).
        const persona = async () => {
          for (let waited = 0; waited < 20000; waited += 250) {
            if (anamRef.current?.auraReady) return anamRef.current;
            if (!avatarConnectingRef.current && !callRef.current && !textVoiceWantedRef.current) break; // none coming
            await new Promise((r) => setTimeout(r, 250));
          }
          return avatarWhenReady();
        };
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const client = await persona();
          if (!client) break;
          try {
            client.sendUserMessage(text);
            typedToPersonaRef.current.push(text);
            setMessages((m) => [...m, { id: randomId(), role: "user", text }]);
            setStatus("thinking");
            return;
          } catch (err) {
            console.warn("Simmi: couldn't reach the Anam persona yet", err); // eslint-disable-line no-console
            await new Promise((r) => setTimeout(r, 1000)); // just connected: try once more
          }
        }
        console.warn("Simmi: the Anam persona isn't available, the backend answers"); // eslint-disable-line no-console
      }
      misheardRef.current = 0;
      // Written or said in another of her languages (its script, Hinglish, or plain English): Simmi switches with
      // them, so her reply, her voice and what she listens for all follow. Romanized or misheard speech in another
      // language is caught by the backend instead (see the reply's `language` below). The finder's message is
      // composed by the page, so it says nothing about the visitor's language.
      if (language) {
        if (language !== langRef.current) await setLanguageRef.current(language); // decided from the audio
      } else if (!finder) {
        const detected = detectLanguage(text);
        if (detected && detected !== langRef.current) await setLanguageRef.current(detected);
      }
      // In a call, a new question interrupts whatever Simmi is saying or preparing, and is answered now.
      if (callRef.current) bargeIn();
      if (sendingRef.current) {
        // Typed chat: a message sent while Simmi is still answering goes next.
        queuedUtteranceRef.current = queuedUtteranceRef.current ? `${queuedUtteranceRef.current} ${text}` : text;
        return;
      }
      const turn = ++turnRef.current;
      const abort = new AbortController();
      turnAbortRef.current = abort;
      const current = () => turnRef.current === turn;
      sendingRef.current = true;
      setSending(true);
      setStatus("thinking");
      setMessages((m) => [...m, { id: randomId(), role: "user", text }]);
      if (callRef.current) {
        setCallState("thinking");
        showCaption("");
      }
      const payload = {
        message: text,
        session_id: sessionRef.current,
        visitor_id: getVisitorId(),
        page: location.pathname,
        // The line Simmi opened with (e.g. the returning-visitor greeting), so the AI knows what "yes" answers.
        opening: sessionRef.current ? undefined : openingRef.current || undefined,
        language: langRef.current,
        // decided from the audio (English spoken to Hindi recognition can arrive in Hindi script): not re-detected
        language_heard: Boolean(language) || undefined,
        finder: finder || undefined,
      };
      const client = await avatarWhenReady(); // waits for a still-connecting avatar so the reply is lip-synced
      if (!current()) return;
      const inCall = callRef.current;
      const typedAndSpoken = !inCall && voiceOnRef.current; // typed chat with the voice on
      const replyId = randomId();
      // The avatar's talk stream; if the avatar refuses (e.g. its connection just dropped), the reply is
      // fetched normally and spoken with the browser voice instead.
      let talk = null;
      if (client && (inCall || typedAndSpoken)) {
        try {
          talk = client.createTalkMessageStream(replyId);
          activeTalkIdRef.current = replyId;
        } catch (err) {
          console.warn("Simmi: avatar can't speak right now, using browser voice", err); // eslint-disable-line no-console
        }
      }
      let reply;
      let actions = [];
      let streamedToAvatar = false;
      let placeholder = false;
      const putReply = (fields) =>
        setMessages((m) =>
          placeholder
            ? m.map((x) => (x.id === replyId ? { ...x, ...fields } : x))
            : [...m, { id: replyId, role: "assistant", actions: [], ...fields }],
        );
      const onStatus = (key) => {
        if (current()) setStatus(key);
      };
      try {
        let res;
        if (talk) {
          // The avatar speaks the reply while it's still being written (Docket-speed turn-taking), and the
          // words appear at her speaking pace. The reply's card joins the conversation now and fills in.
          placeholder = true;
          speakingIdRef.current = replyId;
          setSpeakingId(replyId);
          setMessages((m) => [...m, { id: replyId, role: "assistant", text: "", actions: [], voiced: true }]);
          let queue = Promise.resolve();
          let spoken = "";
          const live = () => current() && anamRef.current === client;
          res = await auraApi.chatStream(
            payload,
            (delta) => {
              if (!current()) return;
              setStatus(null);
              const words = delta.replace(/[*_#`]/g, ""); // markdown isn't spoken
              const first = !spoken;
              spoken += words;
              saying.current = spoken;
              if (inCall) setCallState("speaking");
              followCaption(spoken, first);
              queue = queue.then(() => (live() && words ? talk.streamMessageChunk(words, false) : null));
            },
            abort.signal,
            onStatus,
          );
          queue = queue.then(() => (live() ? talk.endMessage() : null));
          await queue;
          if (!current()) return;
          streamedToAvatar = anamRef.current === client;
        } else {
          // The reply writes itself out as it streams in (typed chat). In a call without the avatar, the browser
          // voice speaks it once complete, so there it's only the status that streams.
          const intoBubble = !inCall;
          let written = "";
          let frame = 0;
          const flush = () => {
            frame = 0;
            if (current()) setMessages((m) => m.map((x) => (x.id === replyId ? { ...x, text: written } : x)));
          };
          if (intoBubble) {
            placeholder = true;
            setMessages((m) => [...m, { id: replyId, role: "assistant", text: "", actions: [], streamed: true }]);
          }
          try {
            res = await auraApi.chatStream(
              payload,
              (delta) => {
                if (!current()) return;
                setStatus(null);
                if (!intoBubble) return;
                written += delta;
                if (!frame) frame = requestAnimationFrame(flush); // one render per frame, not per token
              },
              abort.signal,
              onStatus,
            );
          } finally {
            if (frame) cancelAnimationFrame(frame);
          }
        }
        if (!current()) return;
        sessionRef.current = res.session_id;
        setSessionId(res.session_id);
        if (res.lead?.email) setLeadCaptured(true);
        reply = res.reply;
        // The backend heard another language (e.g. romanized Tamil, or Simmi's set_language): follow it.
        if (res.language && res.language !== langRef.current && LANGUAGES.includes(res.language)) {
          setLanguageRef.current(res.language);
        }
        actions = res.actions || [];
        putReply({ text: reply, actions });
      } catch (err) {
        if (!current()) return; // interrupted by the visitor: this answer was abandoned on purpose
        if (client) console.warn("Simmi: streamed reply failed", err); // eslint-disable-line no-console
        reply = "I couldn't reach the server just now. Please try again in a moment.";
        putReply({ text: reply, actions: [], error: true });
      } finally {
        if (current()) {
          sendingRef.current = false;
          setSending(false);
          setStatus(null);
          turnAbortRef.current = null;
        }
      }
      if (!current()) return;
      // The visitor said goodbye: Simmi's reply is her thank-you, and the conversation closes once it's spoken.
      const ending = Boolean(actions?.some((a) => a.type === "end_call"));
      if (streamedToAvatar) {
        // Already being spoken; wait for the avatar's end-of-speech (with a safety net).
        pendingEndRef.current = ending && inCall;
        followCaption(toSpeech(reply));
        clearTimeout(speakTimerRef.current);
        speakTimerRef.current = setTimeout(avatarDoneSpeaking, 3000 + reply.split(/\s+/).length * 450);
      } else if (inCall) {
        say(reply, ending ? "end" : "listen", replyId);
      } else if (typedAndSpoken) {
        voiceText(reply, replyId); // browser voice, words written in step
      } else if (placeholder) {
        textSpeechDone();
      }
      if (ending && !inCall) {
        // Typed chat: leave the goodbye on screen long enough to read (or hear), then close.
        setTimeout(() => finishRef.current(), 2500 + reply.split(/\s+/).length * 380);
        return;
      }
      const queued = queuedUtteranceRef.current;
      queuedUtteranceRef.current = "";
      if (queued && !ending) sendRef.current(queued);
    },
    [location.pathname, avatarWhenReady, say, avatarDoneSpeaking, followCaption, showCaption, voiceText, textSpeechDone, bargeIn],
  );
  sendRef.current = send;

  const micDenied = useCallback(() => {
    endCall();
    setMessages((m) => [
      ...m,
      {
        id: randomId(),
        role: "assistant",
        text: TEXT[langRef.current].micDenied,
        actions: [],
      },
    ]);
  }, [endCall]);

  // keepState: started while Simmi is speaking or thinking (so she can be interrupted), without showing "listening".
  const listen = useCallback((keepState = false) => {
    if (!callRef.current || !SpeechRecognition || mutedRef.current) return;
    stopListening();
    const rec = new SpeechRecognition();
    rec.lang = speechLang(langRef.current);
    rec.interimResults = true;
    rec.continuous = false;
    let handled = false;
    rec.onresult = (e) => {
      const result = e.results[e.results.length - 1];
      const transcript = Array.from(e.results).map((r) => r[0].transcript).join("");
      if (speakingIdRef.current || sendingRef.current) {
        // She is talking: her own voice through the speakers must not count as the visitor.
        if (isEcho(transcript, saying.current) || isShortEcho(transcript, saying.current)) return;
        if (!result.isFinal && wordsOf(transcript).length < BARGE_IN_WORDS) return;
        bargeInRef.current();
      }
      showCaption(transcript); // the visitor's own words appear as they speak
      if (!result.isFinal) return;
      handled = true;
      if (isUnclear(transcript, result[0].confidence)) clarify();
      else send(transcript, { spoken: true });
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        handled = true;
        micDenied();
      }
    };
    // Silence ends a recognition round; keep listening quietly — the idle flow handles long pauses.
    rec.onend = () => {
      if (recognitionRef.current !== rec) return;
      if (handled || !callRef.current) {
        recognitionRef.current = null; // the next round starts when Simmi answers
        return;
      }
      setTimeout(() => listenRef.current(Boolean(speakingIdRef.current || sendingRef.current)), 250);
    };
    recognitionRef.current = rec;
    if (!keepState) setCallState("listening");
    try {
      rec.start();
    } catch {
      /* already started */
    }
  }, [clarify, send, micDenied, showCaption]);
  listenRef.current = listen;

  // Anam hears one language per session. In English it *translates* other speech to English (a Hindi speaker would
  // get an English answer); in Hindi it writes English speech in Hindi script ("ओके कैन यू टेल मी"). So during an avatar
  // call the browser also listens, in the other language (hi-IN while Anam is in English, en-IN while it's in Hindi),
  // and each utterance's language and words come from the two transcripts together (see understand).
  const sniffRef = useRef(null); // { text, at, from, finals }: what the browser heard of the current utterance
  useEffect(() => {
    if (!callActive || !avatarMode || muted || !SpeechRecognition || MOBILE || config.persona_brain) return undefined;
    const sniff = { text: "", at: 0, from: 0, finals: 0 };
    let rec = null;
    let stopped = false;
    const start = () => {
      rec = new SpeechRecognition();
      rec.lang = lang === "hi" ? "en-IN" : "hi-IN"; // always the opposite of what Anam listens in (see browserHeard)
      rec.continuous = true;
      rec.interimResults = true;
      sniff.from = 0;
      sniff.finals = 0;
      rec.onresult = (e) => {
        const results = Array.from(e.results);
        sniff.finals = results.filter((r) => r.isFinal).length;
        sniff.text = results.slice(sniff.from).map((r) => r[0].transcript).join(" ").trim();
        sniff.at = Date.now();
      };
      rec.onerror = (e) => {
        if (["not-allowed", "service-not-allowed", "audio-capture"].includes(e.error)) stopped = true;
      };
      // Recognition ends on long silences; keep it going for the whole call.
      rec.onend = () => {
        if (!stopped) setTimeout(() => !stopped && start(), 250);
      };
      try {
        rec.start();
      } catch {
        /* already started */
      }
    };
    sniffRef.current = sniff;
    start();
    return () => {
      stopped = true;
      if (sniffRef.current === sniff) sniffRef.current = null;
      if (rec) {
        rec.onend = null;
        rec.abort();
      }
    };
  }, [callActive, avatarMode, muted, lang, config.persona_brain]);
  // What the browser (hi-IN) heard of the visitor's utterance just now, and which language that was (else null).
  // The browser listens in the opposite language to Anam, so every utterance is written down twice: once by Hindi
  // recognition (Hindi in Hindi script; English in English letters or spelled in Hindi script) and once by English
  // recognition (proper English letters). The Hindi-script one tells which language was spoken (spokenLanguage), and
  // the answer is to the transcript written in that language.
  const browserHeard = () => {
    const sniff = sniffRef.current;
    if (!sniff?.text || Date.now() - sniff.at > SNIFF_FRESH_MS) return null;
    const text = sniff.text;
    sniff.text = "";
    return text;
  };
  const understand = (anamText) => {
    const browserText = browserHeard();
    const anamInHindi = langRef.current === "hi";
    const hindiTranscript = anamInHindi ? anamText : browserText;
    const englishTranscript = anamInHindi ? browserText : anamText;
    const language = hindiTranscript ? spokenLanguage(hindiTranscript) : null;
    const hasHindiScript = (t) => /[ऀ-ॿ]/.test(t || "");
    let text = null;
    if (language === "hi") text = hindiTranscript;
    else if (language === "en") {
      // Prefer the English recognition's words; if it has none (it may have been restarting), turn the Hindi-script
      // spelling back into English words.
      text = englishTranscript && !hasHindiScript(englishTranscript) ? englishTranscript : toEnglishWords(hindiTranscript);
    }
    console.info("Simmi heard:", { hindi: hindiTranscript, english: englishTranscript, language: language || "(unknown)", answering: text || anamText }); // eslint-disable-line no-console
    return { text, language };
  };
  const understandRef = useRef(understand);
  understandRef.current = understand;

  // Starting a call faster: the SDK loads in the background once the page is idle, and a session token is
  // fetched as soon as the visitor shows intent (hovers or focuses Simmi), so clicking skips both waits.
  const tokenCacheRef = useRef(null); // { at, promise }
  const prefetchAvatar = useCallback(() => {
    if (!config.avatar) return;
    import("@anam-ai/js-sdk").catch(() => {});
    const cached = tokenCacheRef.current;
    if (cached && cached.lang === langRef.current && Date.now() - cached.at < TOKEN_FRESH_MS) return;
    const entry = { at: Date.now(), lang: langRef.current, promise: auraApi.avatarSession(langRef.current) };
    entry.promise.catch(() => {
      if (tokenCacheRef.current === entry) tokenCacheRef.current = null;
    });
    tokenCacheRef.current = entry;
  }, [config.avatar]);
  const takeAvatarToken = () => {
    const cached = tokenCacheRef.current;
    tokenCacheRef.current = null; // a token starts one session only
    const language = langRef.current;
    if (cached && cached.lang === language && Date.now() - cached.at < TOKEN_FRESH_MS) {
      return cached.promise.catch(() => auraApi.avatarSession(language));
    }
    return auraApi.avatarSession(language);
  };
  useEffect(() => {
    if (!config.avatar) return undefined;
    const timer = setTimeout(() => import("@anam-ai/js-sdk").catch(() => {}), 3000);
    return () => clearTimeout(timer);
  }, [config.avatar]);

  // Connect the Anam avatar. Resolves false if it can't (no key, network, mic), so we can fall back.
  // mic=false is for typed chat: the avatar speaks replies but never opens the microphone.
  const connectAvatar = useCallback(async (mic = true) => {
    const wanted = () => (mic ? callRef.current : textVoiceWantedRef.current && !callRef.current);
    try {
      await closingRef.current; // never overlap the previous session
      if (!wanted()) return false;
      const token = await takeAvatarToken();
      if (!wanted()) return false;
      const { createClient, AnamEvent } = await import("@anam-ai/js-sdk");
      const client = createClient(token, mic ? undefined : { disableInputAudio: true });
      personaHasSpokenRef.current = false; // a new session: the persona greets first
      personaGreetingIdRef.current = null;

      // The Anam Lab persona's book_meeting tool (a client tool): once the visitor has given and confirmed their
      // name, email and phone, the persona calls it; the booking is saved by the backend (backend/logs/meetings.jsonl)
      // and the result goes back to the persona, which confirms it or asks again for a detail that was wrong.
      client.registerToolCallHandler?.("book_meeting", {
        onStart: async ({ arguments: details = {} }) => {
          try {
            const result = await auraApi.bookMeeting({
              ...details,
              channel: callRef.current ? "voice" : "chat", // how it was booked (LeadX source is always "Website")
              page: window.location.pathname,
              session_id: sessionRef.current || "",
              conversation: getConversation(),
            });
            if (!result.ok) return `Not saved: ${result.reason}`;
            setMeetingBooked(true);
            return `Saved. Booking reference ${result.id}. The team will contact them to confirm a time.`;
          } catch {
            return "Not saved: the booking system couldn't be reached. Apologise and suggest emailing contact@simpel.ai.";
          }
        },
      });

      // The visitor finished a sentence: Anam's transcript arrives in the message history.
      client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, (history) => {
        if (personaBrainRef.current) {
          // The persona finished a sentence: show its replies exactly as Anam recorded them (fills in any piece of
          // the live stream that was missed, e.g. the first word).
          // Several persona messages in a row make one bubble (see personaBubbleRef); a whole message the live stream
          // missed is added back.
          const bubbles = new Map(); // bubble id -> its text, from Anam's record
          let bubble = null;
          for (const m of history) {
            if (m.role === "user") {
              bubble = null;
              continue;
            }
            const own = `anam-${m.id}`;
            if (own === personaGreetingIdRef.current) continue; // a booking call's hidden greeting
            const id = personaBubbleRef.current.get(own) || bubble || own;
            if (id !== own) personaBubbleRef.current.set(own, id);
            bubble = id;
            const text = stripModelTags(m.content) + (m.interrupted ? "…" : "");
            bubbles.set(id, bubbles.has(id) ? `${bubbles.get(id).trimEnd()} ${text.trimStart()}` : text);
          }
          // The persona's opening bubble (before the visitor has said anything) is its greeting: restore its start.
          const firstUser = history.findIndex((m) => m.role === "user");
          const opening = history.find((m, i) => m.role !== "user" && (firstUser < 0 || i < firstUser));
          if (opening) {
            const id = personaBubbleRef.current.get(`anam-${opening.id}`) || `anam-${opening.id}`;
            if (bubbles.has(id)) bubbles.set(id, withGreetingStart(bubbles.get(id), personaGreetingRef.current));
          }
          setMessages((ms) => {
            const out = ms.filter((x) => x.id !== "greeting").map((x) => (bubbles.has(x.id) ? { ...x, text: bubbles.get(x.id) } : x));
            const shown = new Set(out.map((x) => x.id));
            for (const [id, text] of bubbles) {
              if (!shown.has(id)) out.push({ id, role: "assistant", text, actions: [], voiced: true });
            }
            return out;
          });
        }
        const last = history[history.length - 1];
        if (!last || last.role !== "user" || handledUserMsgRef.current.has(last.id)) return;
        handledUserMsgRef.current.add(last.id);
        heardRef.current = "";
        if (personaBrainRef.current) {
          // The persona answers on its own: just show what the visitor said (typed messages are already shown).
          console.info("Simmi: Anam heard the visitor say:", JSON.stringify(last.content)); // eslint-disable-line no-console
          const typed = typedToPersonaRef.current.indexOf(last.content.trim());
          if (typed >= 0) typedToPersonaRef.current.splice(typed, 1);
          else setMessages((m) => [...m, { id: randomId(), role: "user", text: last.content }]);
          showCaption("");
          return;
        }
        if (speakingIdRef.current && isEcho(last.content, saying.current)) {
          console.warn("Simmi: ignored her own voice picked up by the microphone:", last.content); // eslint-disable-line no-console
          return;
        }
        // The language comes from what the browser heard (see sniffRef). Hindi: send its Hindi-script words (Anam's
        // English transcript would be a translation). English: send Anam's transcript if it's in English letters.
        const heard = understandRef.current(last.content);
        if (heard.text) send(heard.text, { spoken: true, language: heard.language });
        else if (isUnclear(last.content, 0)) clarify();
        else send(last.content, { spoken: true });
      });
      client.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (event) => {
        if (personaBrainRef.current) {
          if (event.role === "user") {
            heardRef.current = event.contentIndex === 0 ? event.content : heardRef.current + event.content;
            setCallState("listening");
            showCaption(heardRef.current); // the visitor's own words appear as they speak
            return;
          }
          // The persona's reply, word by word as she says it (its greeting first, replacing the written one).
          const id = `anam-${event.id}`;
          // A booking call hides the persona's general greeting (recognised by its text, so the booking reply that
          // follows is never hidden).
          const start = (event.content || "").trim().slice(0, 12);
          const isGreeting =
            personaGreetingIdRef.current === id ||
            (!personaGreetingIdRef.current && start.length >= 2 && (personaGreetingRef.current || "").includes(start));
          if (skipGreetingRef.current && isGreeting) {
            if (personaGreetingIdRef.current !== id) {
              personaGreetingIdRef.current = id;
              try {
                client.interruptPersona(); // the greeting has just started: stop it, it's not heard
              } catch {
                /* already over */
              }
              setTimeout(() => flushBookingAskRef.current(), 400); // then the visitor's booking request
            }
            return;
          }
          if (!event.contentIndex) console.info("Simmi persona says:", { id: event.id, first: event.content }); // eslint-disable-line no-console
          setMessages((ms) => {
            const base = ms.filter((m) => m.id !== "greeting");
            const target = personaBubbleRef.current.get(id) || id;
            const at = base.findIndex((m) => m.id === target);
            const piece = stripModelTags(event.content) + (event.interrupted ? "…" : "");
            if (at < 0) {
              // A new persona message right after another one (the visitor hasn't spoken): it joins that bubble.
              const last = base[base.length - 1];
              if (last?.role === "assistant" && String(last.id).startsWith("anam-")) {
                personaBubbleRef.current.set(id, last.id);
                const copy = [...base];
                copy[copy.length - 1] = { ...last, text: `${last.text.trimEnd()} ${piece.trimStart()}` };
                return copy;
              }
              // The persona's first words in this conversation are its greeting: restore any start Anam dropped.
              const opening = !base.some((m) => m.role === "user" || String(m.id).startsWith("anam-"));
              const text = opening ? withGreetingStart(piece, personaGreetingRef.current) : piece;
              return [...base, { id, role: "assistant", text, actions: [], voiced: true }];
            }
            const copy = [...base];
            copy[at] = { ...copy[at], text: copy[at].text + piece };
            return copy;
          });
          setStatus(null);
          if (personaTurnRef.current.id !== id) personaTurnRef.current = { id, text: "" };
          personaTurnRef.current.text += event.content || "";
          if (event.content) setCallState("speaking");
          if (event.endOfSpeech || event.interrupted) {
            setCallState("listening");
            personaHasSpokenRef.current = true;
            // "please type your phone number in the chat box": open the typing box for them
            const said = personaTurnRef.current.text;
            if (/\btype\b|टाइप/i.test(said) && /chat|box|phone|number|email|mail|नंबर|ईमेल/i.test(said)) {
              setTypeRequestAt(Date.now());
            }
          }
          return;
        }
        if (event.role === "user") {
          heardRef.current = event.contentIndex === 0 ? event.content : heardRef.current + event.content;
          const heard = heardRef.current;
          if (speakingIdRef.current || sendingRef.current) {
            // Visitor talking over Simmi: once it's clearly them (not her echo), she stops and listens.
            if (wordsOf(heard).length < BARGE_IN_WORDS || isEcho(heard, saying.current)) return;
            bargeInRef.current();
          }
          setCallState("listening");
          showCaption(heard); // the visitor's own words appear as they speak
        } else if (event.endOfSpeech) {
          // Ignore the end of a reply that was cut off: it must not end the answer that replaced it.
          const stale =
            event.interrupted ||
            (activeTalkIdRef.current && event.correlationId && event.correlationId !== activeTalkIdRef.current);
          if (!stale) avatarDoneSpeaking();
        }
      });
      client.addListener(AnamEvent.USER_SPEECH_STARTED, () => {
        heardRef.current = "";
        // A new utterance: the Hindi listener forgets finished ones (and Simmi's own voice it picked up).
        const sniff = sniffRef.current;
        if (sniff) {
          sniff.from = sniff.finals;
          sniff.text = "";
        }
        if (!speakingIdRef.current && !sendingRef.current) showCaption(""); // don't blank her words mid-answer
      });
      client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, (correlationId) => {
        if (activeTalkIdRef.current && correlationId !== activeTalkIdRef.current) return; // an older reply
        avatarDoneSpeaking();
      });
      client.addListener(AnamEvent.MIC_PERMISSION_DENIED, micDenied);
      // streamToVideoElement resolves before the WebRTC peer and Anam's engine are ready; talking earlier
      // fails with "peer connection is null". Wait for both signals (or proceed once connected, after a grace period).
      const ready = new Promise((resolve, reject) => {
        let connected = false;
        let sessionReady = false;
        const done = () => connected && sessionReady && resolve();
        const timer = setTimeout(
          () => (connected ? resolve() : reject(new Error("Avatar connection timed out"))),
          AVATAR_CONNECT_TIMEOUT_MS,
        );
        client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
          connected = true;
          done();
        });
        client.addListener(AnamEvent.SESSION_READY, () => {
          sessionReady = true;
          done();
        });
        client.addListener(AnamEvent.CONNECTION_CLOSED, (reason, details) => {
          clearTimeout(timer);
          reject(new Error(`Avatar connection closed: ${reason}${details ? ` (${details})` : ""}`));
        });
      });
      ready.catch(() => {}); // handled below; avoid an unhandled rejection if the connection closes later

      let live = false; // set once connected; closes before that are handled by the fallback below
      client.addListener(AnamEvent.CONNECTION_CLOSED, (reason, details) => {
        console.warn("Simmi: avatar connection closed", reason, details || ""); // eslint-disable-line no-console
        if (!live || anamRef.current !== client) return;
        anamRef.current = null;
        setAvatarMode(false);
        if (mic && callRef.current) endCall();
        if (!mic) setTextVoice("off"); // typed replies carry on with the browser voice
      });

      anamRef.current = client;
      setAvatarMode(true); // renders the <video> element the stream attaches to
      await new Promise((r) => requestAnimationFrame(r));
      await client.streamToVideoElement(AVATAR_VIDEO_ID);
      await ready;
      live = true;
      client.auraReady = true; // only now may Simmi speak through it (see readyAvatar)
      if (wanted() && anamRef.current === client) return true;
      if (anamRef.current === client) {
        // No longer wanted (chat closed or a call started meanwhile): don't leave it running.
        anamRef.current = null;
        setAvatarMode(false);
        closeAvatar(client);
      }
      return false;
    } catch (err) {
      console.warn("Simmi: avatar unavailable, using browser voice", err); // eslint-disable-line no-console
      if (anamRef.current) closeAvatar(anamRef.current);
      anamRef.current = null;
      setAvatarMode(false);
      return false;
    }
  }, [clarify, send, avatarDoneSpeaking, micDenied, endCall, showCaption, getConversation]);

  // Typed chat's avatar voice: connected while the chat is open with the voice on, closed otherwise.
  const stopTextVoice = useCallback(() => {
    textVoiceWantedRef.current = false;
    setTextVoice("off");
    textSpeechDone();
    speechRunRef.current += 1; // stops the rest of a sentence-by-sentence reply too
    window.speechSynthesis?.cancel();
    if (!callRef.current && anamRef.current) {
      const client = anamRef.current;
      anamRef.current = null;
      setAvatarMode(false);
      closeAvatar(client);
    }
  }, [textSpeechDone]);

  const startTextVoice = useCallback(async () => {
    if (textVoiceWantedRef.current || callRef.current || anamRef.current) return;
    textVoiceWantedRef.current = true;
    setTextVoice("connecting");
    // Replies sent while this is connecting wait for it (see avatarWhenReady), so they're lip-synced.
    const connecting = connectAvatar(false);
    avatarConnectingRef.current = connecting;
    const ok = await connecting;
    if (avatarConnectingRef.current === connecting) avatarConnectingRef.current = null;
    if (textVoiceWantedRef.current) setTextVoice(ok ? "on" : "off");
  }, [connectAvatar]);

  // Switch Simmi's language (the EN / हिं button, or a typed message in the other language). Speech recognition
  // and the browser voice follow at once. The avatar switches what it listens for mid-session; with a separate
  // Hindi voice configured it reconnects with that voice (a few seconds, once per switch).
  const setLanguage = useCallback(
    async (next) => {
      if (!LANGUAGES.includes(next) || next === langRef.current) return;
      const previous = langRef.current;
      langRef.current = next;
      setLang(next);
      voiceRef.current = pickVoice(next);
      setMessages((ms) => ms.map((m) => (m.id === "greeting" ? greetingMessage(next) : m)));
      tokenCacheRef.current = null; // prefetched tokens were for the other language
      if (recognitionRef.current) listenRef.current(Boolean(speakingIdRef.current || sendingRef.current));
      let client = anamRef.current;
      if (!client?.auraReady) return;
      // Languages with their own avatar voice (ANAM_VOICE_ID_<CODE> on the backend); the rest share the default.
      const voiced = config.language_voices || (config.hindi_voice ? ["hi"] : []);
      if (!voiced.includes(next) && !voiced.includes(previous)) {
        // Same voice: Anam just starts listening in the new language, mid-session.
        try {
          client.sendDataMessage(JSON.stringify({ message_type: "persona_config", data: { languageCode: next } }));
        } catch (err) {
          console.warn("Simmi: couldn't switch the avatar's language", err); // eslint-disable-line no-console
        }
        return;
      }
      // A different voice needs a new avatar session. Let her finish the sentence she's saying first (a reply in the
      // new language may already be playing), for up to 20 seconds.
      for (let waited = 0; (speakingIdRef.current || sendingRef.current) && waited < 20000; waited += 300) {
        await new Promise((r) => setTimeout(r, 300));
      }
      if (langRef.current !== next) return; // switched again meanwhile
      client = anamRef.current;
      if (!client) return;
      const inCall = callRef.current;
      anamRef.current = null;
      setAvatarMode(false);
      closeAvatar(client);
      if (inCall) {
        const ok = await connectAvatar(true);
        if (!ok && callRef.current && SpeechRecognition && !recognitionRef.current) listenRef.current();
      } else if (textVoiceWantedRef.current) {
        setTextVoice("connecting");
        const connecting = connectAvatar(false);
        avatarConnectingRef.current = connecting;
        const ok = await connecting;
        if (avatarConnectingRef.current === connecting) avatarConnectingRef.current = null;
        if (textVoiceWantedRef.current) setTextVoice(ok ? "on" : "off");
      }
    },
    [config.hindi_voice, config.language_voices, connectAvatar],
  );
  setLanguageRef.current = setLanguage;

  // The visitor picked a language (the picker, or the chips Simmi offers when she can't make out their speech).
  // In a call she confirms in that language that she's listening.
  const chooseLanguage = useCallback(
    async (next) => {
      misheardRef.current = 0;
      await setLanguage(next);
      if (callRef.current) sayRef.current(TEXT[next].listening);
    },
    [setLanguage],
  );

  const startCall = useCallback(async (surface = "panel") => {
    // A booking call (requestMeeting) hides the persona's greeting for the whole session; any other call greets.
    skipGreetingRef.current = bookingCallRef.current;
    bookingCallRef.current = false;
    if ((!SpeechRecognition && !config.avatar) || callRef.current) return;
    if (anamRef.current || textVoiceWantedRef.current) stopTextVoice(); // a call needs the mic-enabled avatar
    setCallSurface(["hero", "dock"].includes(surface) ? surface : "panel");
    setReviewing(false);
    setHidden(false);
    callRef.current = true;
    const gen = ++callGenRef.current;
    const stillThisCall = () => callRef.current && callGenRef.current === gen;
    setCallActive(true);
    setTalked(true);
    setCallState("idle");
    const talkedBefore = messages.some((m) => m.role === "user");
    // The spoken greeting replaces the written one, and any opening from an earlier attempt that dropped
    // before the visitor spoke (otherwise the greeting showed twice).
    if (!talkedBefore) setMessages((m) => m.filter((x) => x.id !== "greeting" && x.kind !== "voice"));

    // Personalised opening (returning visitors are greeted by memory), fetched while the avatar connects.
    const greetingRequest = talkedBefore ? null : auraApi.greeting(getVisitorId(), langRef.current).catch(() => null);
    const tried = Date.now();
    let avatarReady = config.avatar && (await connectAvatar());
    // A quick refusal usually means the previous session hasn't been released yet: wait a moment and try once
    // more, so Simmi keeps her own voice. A slow failure (timeout) isn't retried: the visitor has waited enough.
    if (config.avatar && !avatarReady && stillThisCall() && Date.now() - tried < AVATAR_QUICK_FAIL_MS) {
      await new Promise((r) => setTimeout(r, AVATAR_RETRY_MS));
      if (stillThisCall()) avatarReady = await connectAvatar();
    }
    if (!stillThisCall()) return;
    if (!avatarReady && !SpeechRecognition) {
      micDenied();
      return;
    }
    if (avatarReady && personaBrainRef.current) return; // the Anam Lab persona greets the visitor itself
    if (talkedBefore) {
      // Back on a call in the same conversation: a short spoken cue, not another line in the thread.
      if (callRef.current) say(TEXT[langRef.current].listening);
      return;
    }
    const opening = (await greetingRequest)?.text || TEXT[langRef.current].voiceGreeting;
    if (!stillThisCall()) return;
    if (!sessionRef.current) openingRef.current = opening;
    addAssistant(opening, "voice");
  }, [messages, addAssistant, say, config.avatar, connectAvatar, micDenied, stopTextVoice]);

  useEffect(() => () => endCall(), [endCall]);

  // Close the avatar session when the visitor leaves or reloads the page. Otherwise Anam keeps the session
  // open until it times out, and with a concurrent-session limit (1 on the free plan) the next visit can't connect.
  useEffect(() => {
    const close = () => anamRef.current?.stopStreaming().catch(() => {});
    window.addEventListener("pagehide", close);
    return () => window.removeEventListener("pagehide", close);
  }, []);

  // Open the avatar voice for typed chat while it's open with the voice on (no call running).
  useEffect(() => {
    if (open && voiceOn && config.avatar && !callActive) startTextVoice();
    else if (!callActive) stopTextVoice();
  }, [open, voiceOn, config.avatar, callActive, startTextVoice, stopTextVoice]);

  const reset = useCallback(() => {
    endCall();
    setLanguageRef.current("en"); // the next conversation starts in English, whatever this one was in
    sessionRef.current = null;
    openingRef.current = null;
    setSessionId(null);
    setMessages([greetingMessage("en")]);
    setMeetingBooked(false);
    setLeadCaptured(false);
    setTalked(false);
    setReviewing(false);
  }, [endCall]);

  // Hanging up (or saying goodbye) ends the conversation and hides the agent (like Docket): Simmi collapses to
  // the small "Talk to Simmi" button, and talking again starts a fresh conversation with a new greeting.
  const hangUp = useCallback(() => {
    reset();
    queuedUtteranceRef.current = "";
    handledUserMsgRef.current = new Set();
    setOpen(false);
    setHidden(true);
  }, [reset]);
  finishRef.current = hangUp;

  // The "Book a Meeting" button: Simmi opens and starts booking (asks name, email and phone). With the Anam Lab
  // persona this goes to the persona; otherwise the backend's reply carries the booking form.
  // Sends the waiting booking request once (when the greeting is stopped, or after a few seconds without one).
  flushBookingAskRef.current = () => {
    const ask = pendingBookingAskRef.current;
    pendingBookingAskRef.current = null;
    bookingStartingRef.current = false;
    if (ask && callRef.current) sendRef.current(ask);
  };

  const requestMeeting = useCallback(async () => {
    if (bookingStartingRef.current) return; // already starting: a second click must not send the request twice
    setHidden(false);
    const ask = (TEXT[langRef.current] || TEXT.en).bookMeetingAsk || TEXT.en.bookMeetingAsk;
    // With the Anam Lab persona, booking is by voice: start a call without the general greeting, then ask her to book.
    if (config.persona_brain && config.avatar) {
      if (callRef.current) {
        sendRef.current(ask); // already talking: just ask
        return;
      }
      bookingStartingRef.current = true;
      bookingCallRef.current = true; // no general greeting: the visitor came to book
      pendingBookingAskRef.current = ask; // sent when the greeting is stopped (see the persona stream handler)
      await startCall("dock");
      if (callRef.current) {
        // The persona may not greet at all: then the request goes after a few seconds.
        setTimeout(() => flushBookingAskRef.current(), 5000);
        return;
      }
      // The call didn't start (e.g. no microphone): continue in the typed chat instead.
      pendingBookingAskRef.current = null;
      bookingStartingRef.current = false;
    }
    setOpen(true);
    sendRef.current(ask);
  }, [config.persona_brain, config.avatar, startCall]);

  const recordEvent = useCallback(async (body) => {
    if (!sessionRef.current) return;
    await auraApi.event(sessionRef.current, body);
    if (body.type === "meeting_booked") setMeetingBooked(true);
    if (body.type === "email_submitted") setLeadCaptured(true);
  }, []);

  // The Opportunity Finder card under this message was answered: it collapses to a one-line summary.
  const finishFinder = useCallback((id, summary) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, finderDone: summary } : m)));
  }, []);

  // 👍 / 👎 on one of Simmi's replies (kept on the message so it shows as chosen).
  const rateMessage = useCallback(
    (message, rating) => {
      if (message.rating === rating) return;
      setMessages((ms) => ms.map((m) => (m.id === message.id ? { ...m, rating } : m)));
      recordEvent({ type: "feedback", rating, reply: toSpeech(message.text).slice(0, 300) }).catch(() => {});
    },
    [recordEvent],
  );

  // Save the typed conversation for a refresh (see SAVED_CHAT). Nothing is kept until the visitor has said something.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!messages.some((m) => m.role === "user")) {
        storage.set("sessionStorage", SAVED_CHAT, "");
        return;
      }
      const kept = messages
        .filter((m) => m.text || m.actions?.length)
        .slice(-SAVED_MAX_MESSAGES)
        // Restored replies show in full at once (no retyping) and nothing is mid-speech.
        .map(({ id, role, text, actions, kind, rating, finderDone }) => ({
          id, role, text, actions, kind, rating, finderDone, voiced: true, streamed: true,
        }));
      storage.set(
        "sessionStorage",
        SAVED_CHAT,
        JSON.stringify({ v: 1, savedAt: Date.now(), sessionId, messages: kept, meetingBooked, leadCaptured, open, lang }),
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [messages, sessionId, meetingBooked, leadCaptured, open, lang]);

  // Ctrl/⌘+K opens Simmi's text chat from anywhere (or focuses it); Esc closes it when the focus is inside it.
  useEffect(() => {
    const inSimmi = () => Boolean(document.activeElement?.closest?.("[data-simmi]"));
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
        if (callRef.current) return;
        e.preventDefault();
        if (open && inSimmi()) {
          setOpen(false);
          return;
        }
        setHidden(false);
        setOpen(true);
        setTimeout(() => window.dispatchEvent(new Event("simmi:focus")), 60); // once the composer has rendered
      } else if (e.key === "Escape" && open && !callRef.current && inSimmi()) {
        setOpen(false);
        if (config.avatar) setHidden(true); // the dock collapses to its button, as its ✕ does
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, config.avatar]);

  // A voice session counts as a conversation from the first word Simmi says (and stays one after it ends).
  const hasConversation = callActive || talked || messages.some((m) => m.role === "user");
  const chatVisible = open || (location.pathname === "/" && heroVisible && hasConversation);

  // Idle flow: gentle check-in after a pause, then politely end the session.
  useEffect(() => {
    const last = messages[messages.length - 1];
    const engaged = hasConversation;
    if (!engaged || sending || !last || last.role !== "assistant" || last.kind === "end") return undefined;
    if (config.persona_brain) return undefined; // the Anam Lab persona leads the conversation (no check-ins of Simmi's own)
    if (!callActive && !chatVisible) return undefined;
    if ((callActive && callState === "speaking") || speakingId) return undefined; // count once Simmi stops talking
    // A visitor typing (even mid-call) is present: give them the longer, text-chat patience.
    const typing = Date.now() - typingAt < IDLE.text.nudge;
    const timing = callActive && !typing ? IDLE.voice : IDLE.text;
    const nudged = last.kind === "nudge";
    const timer = setTimeout(() => {
      if (!callActive && document.hidden) return;
      if (nudged) {
        addAssistant(TEXT[langRef.current].end, "end", "end");
      } else {
        const offeredMeeting = messages.some((m) => m.actions?.some((a) => a.type === "book_meeting"));
        addAssistant(TEXT[langRef.current][offeredMeeting && !leadCaptured ? "nudgeMeeting" : "nudge"], "nudge");
      }
    }, nudged ? timing.end : timing.nudge);
    return () => clearTimeout(timer);
    // caption: live speech resets the idle clock; typingAt: so does typing
  }, [messages, sending, callActive, callState, caption, chatVisible, hasConversation, leadCaptured, addAssistant, speakingId, typingAt, config.persona_brain]);

  const value = {
    messages,
    sending,
    status,
    send,
    finishFinder,
    rateMessage,
    config,
    open,
    setOpen,
    heroVisible,
    setHeroVisible,
    voiceOn,
    setVoiceOn,
    meetingBooked,
    setMeetingBooked,
    leadCaptured,
    recordEvent,
    reset,
    hangUp,
    requestMeeting,
    typeRequestAt,
    prefetchAvatar,
    reviewing,
    setReviewing,
    hidden,
    setHidden,
    outputMuted,
    setOutputMuted,
    hasConversation,
    sessionId,
    getConversation,
    callActive,
    callState,
    caption,
    startCall,
    endCall,
    avatarMode,
    callSurface,
    speakingId,
    textVoice,
    noteTyping,
    muted,
    toggleMute,
    canTalk: Boolean(SpeechRecognition) || Boolean(config.avatar),
    lang,
    setLanguage,
    chooseLanguage,
    t,
  };
  return <AuraContext.Provider value={value}>{children}</AuraContext.Provider>;
}

export const useAura = () => useContext(AuraContext);
